import { taskSkillSessionHeaders } from './features/task-skills/session';
import { BACKEND_URL, ADMIN_API_KEY_ENCRYPTION_SECRET, ADMIN_API_KEY } from './helpers';

const FRONTEND_ADMIN_API_KEY = ADMIN_API_KEY;
const FRONTEND_ADMIN_API_ENCRYPTION_SECRET = ADMIN_API_KEY_ENCRYPTION_SECRET;
const DEAL_STUDIO_API_KEY = process.env.REACT_APP_DEAL_STUDIO_API_KEY || 'pit-3053bfef-fa63-488b-bfa3-d09ca9e3904a';
let encryptedApiKeyCache = null;

const toBase64 = (byteArray) => {
  let binary = '';
  byteArray.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary);
};

const encryptAdminApiKey = async () => {
  if (!FRONTEND_ADMIN_API_KEY || !FRONTEND_ADMIN_API_ENCRYPTION_SECRET) {
    return null;
  }

  if (!window?.crypto?.subtle) {
    return null;
  }

  const encoder = new TextEncoder();
  const secretHash = await window.crypto.subtle.digest('SHA-256', encoder.encode(FRONTEND_ADMIN_API_ENCRYPTION_SECRET));
  const cryptoKey = await window.crypto.subtle.importKey(
    'raw',
    secretHash,
    { name: 'AES-GCM' },
    false,
    ['encrypt']
  );

  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const encryptedBuffer = await window.crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    cryptoKey,
    encoder.encode(FRONTEND_ADMIN_API_KEY)
  );

  const encryptedBytes = new Uint8Array(encryptedBuffer);
  const tag = encryptedBytes.slice(encryptedBytes.length - 16);
  const ciphertext = encryptedBytes.slice(0, encryptedBytes.length - 16);

  return `${toBase64(iv)}.${toBase64(ciphertext)}.${toBase64(tag)}`;
};

const getEncryptedApiKey = async () => {
  if (!encryptedApiKeyCache) {
    encryptedApiKeyCache = encryptAdminApiKey();
  }
  return encryptedApiKeyCache;
};

// Module-level ref to the Redux store. Set once at app bootstrap via
// _setBackendStoreRef (below). Avoids importing the store directly here,
// which would create a circular dependency.
let _storeRef = null;
const _setBackendStoreRef = (s) => { _storeRef = s; };

// Direct override set by DealPartnerProvider when it resolves the current
// partner from Stytch email. Most pages don't dispatch SHOW_EMPLOYEE, so
// employeeStore.deal_partner is often null — this is the canonical source.
let _currentDealPartnerIdOverride;
const _setCurrentDealPartnerId = (id) => { _currentDealPartnerIdOverride = id || null; };

const getDealPartnerId = () => {
  if (_currentDealPartnerIdOverride !== undefined) return _currentDealPartnerIdOverride;
  const state = _storeRef?.getState?.();
  return state?.employeeStore?.simulated_deal_partner?.id
      || state?.employeeStore?.deal_partner?.id
      || null;
};
const _getCurrentDealPartnerId = () => getDealPartnerId();

const formatHeaders = async (sendToken, method, body=null, extraHeaders = {}, path = '' ) => {
  // Capture identity before awaiting encryption so a view change cannot retarget an in-flight request.
  const dpId = getDealPartnerId();
  const workspaceSession = /\/admin\/(?:my_workspace|workspace_agent_connections|buyer_mandate_relationships)(?:\/|$|\?)/.test(path) ? taskSkillSessionHeaders() : {};
  const encryptedApiKey = await getEncryptedApiKey();
  const headers = {
    Accept: 'application/json',
    'Content-Type': 'application/json'
  };

  if (encryptedApiKey) {
    headers['X-Encrypted-Api-Key'] = encryptedApiKey;
  }

  if (dpId) {
    headers['X-Deal-Partner-Id'] = dpId;
  }

  if (sendToken) {
    return (
      {
        headers: {
          ...headers,
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          ...extraHeaders,
          ...workspaceSession
        },
        method: method,
        body: body
      }
    )
  } else {
    return (
      {
        headers: {
          ...headers,
          ...extraHeaders,
          ...workspaceSession
        },
        method: method,
        body: body
      }
    )
  }
}

async function get(params, sendToken=false, extraHeaders = {}) {
  const url = BACKEND_URL + params
  return fetch(url, await formatHeaders(sendToken, "GET", null, extraHeaders, params)).then(function(response) {
    return response;
  });
}

async function post(params, body=null, sendToken=false, extraHeaders = {}) {
  const url = BACKEND_URL + params
  return fetch(url, await formatHeaders(sendToken, "POST", body, extraHeaders, params)).then(function(response) {
    return response;
  });
}

async function patch(params, body=null, sendToken=false, extraHeaders = {}) {
  const url = BACKEND_URL + params
  return fetch(url, await formatHeaders(sendToken, "PATCH", body, extraHeaders, params)).then(function(response) {
    return response;
  });
}

async function deleteRequest(params, body=null, sendToken=false) {
  if (typeof body === 'boolean') {
    sendToken = body;
    body = null;
  }

  const url = BACKEND_URL + params
  return fetch(url, await formatHeaders(sendToken, "DELETE", body, {}, params)).then(function(response) {
    return response;
  });
}

// SLACK NOTIFICATIONS

function slackNotificationCreate(data) {
  return post('/api/v1/slack_notifications', JSON.stringify({
    message: data
  }));
}

// N8N WORKFLOWS

function n8nWorkflowRun(workflow) {
  let url
  switch (workflow) {
    case 'copy-financial-table':
      url = "https://cambutcher.app.n8n.cloud/webhook/18fd1145-8507-4f19-89b8-dd3b6419a347"
      break
    case 'copy-exitNavigator-report':
      url = "https://cambutcher.app.n8n.cloud/webhook/5eaf0114-6437-45a6-96ec-6027c3b0f250"
      break
    default:
      return Promise.reject(new Error(`Unknown n8n workflow: ${workflow}`));
  }

  return fetch(url, {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    },
    method: "POST",
    body: null
  }).then(function(response) {
    return response;
  });
}

// BUYERS

function buyersIndex(query=null) {
  return get(`/admin/buyers?${query}`);
}

function searchBuyerDatabase(query, page = 1, lens = null, filters = {}) {
  const params = new URLSearchParams({ query, page: String(page) });
  if (lens) params.set('lens', lens);
  Object.entries(filters || {}).forEach(([key, value]) => {
    const values = Array.isArray(value) ? value : [value];
    values.filter(Boolean).forEach((item) => params.append(`filters[${key}][]`, item));
  });
  return get(`/admin/buyers/search_database?${params.toString()}`);
}

// One request for the My Buyer Database map: facets and the matching
// buyers. Blank params are left out so the API applies its defaults.
function buyerDatabaseMap(params = {}) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value)) value.filter(Boolean).forEach((item) => search.append(`${key}[]`, String(item)));
    else if (value !== null && value !== undefined && value !== '') search.set(key, String(value));
  });
  return get(`/admin/buyers/search_database/map?${search.toString()}`);
}

function buyerShow(buyerId) {
  return get(`/admin/buyers/${buyerId}`);
}

// SELLERS

function sellersIndex(query=null) {
  return get(`/admin/sellers?${query}`);
}

export function saveSellerContact(sellerId, contactId, contact) {
  const path = `/admin/sellers/${encodeURIComponent(sellerId)}/contacts${contactId ? `/${encodeURIComponent(contactId)}` : ''}`;
  return contactId ? patch(path, JSON.stringify({ contact }), true) : post(path, JSON.stringify({ contact }), true);
}

export function getSellerContext(sellerId) {
  return get(`/admin/sellers/${encodeURIComponent(sellerId)}/context`, true);
}

export async function getNdaReviewAccuracy(params = { days: 30 }, signal) {
  const path = `/admin/nda_review_accuracy?${new URLSearchParams(params)}`;
  const sessionHeaders = taskSkillSessionHeaders();
  return fetch(BACKEND_URL + path, { ...await formatHeaders(true, 'GET', null, sessionHeaders, path), cache: 'no-store', signal });
}

export async function summarizeNdaReasoning(actionId, signal) {
  const path = `/admin/nda_review_accuracy/actions/${encodeURIComponent(actionId)}/reasoning_summary`;
  return fetch(BACKEND_URL + path, { ...await formatHeaders(true, 'POST', JSON.stringify({}), taskSkillSessionHeaders(), path), cache: 'no-store', signal });
}

export async function getNdaAccuracyDisagreements(partnerId, params, signal) {
  const path = `/admin/nda_review_accuracy/partners/${encodeURIComponent(partnerId)}/disagreements?${new URLSearchParams(params)}`;
  const sessionHeaders = taskSkillSessionHeaders();
  return fetch(BACKEND_URL + path, { ...await formatHeaders(true, 'GET', null, sessionHeaders, path), cache: 'no-store', signal });
}

async function dataroomLifecycleGet(sellerId, resource, signal) {
  const path = `/admin/sellers/${encodeURIComponent(sellerId)}/dataroom_lifecycle${resource}`;
  const sessionHeaders = taskSkillSessionHeaders();
  return fetch(BACKEND_URL + path, { ...await formatHeaders(true, 'GET', null, sessionHeaders, path), cache: 'no-store', signal });
}

export const getDataroomDriveScan = (sellerId, signal) => dataroomLifecycleGet(sellerId, '/drive_scan', signal);
export const requestDataroomDriveScan = sellerId => post(`/admin/sellers/${encodeURIComponent(sellerId)}/dataroom_lifecycle/drive_scan`, JSON.stringify({}), true, taskSkillSessionHeaders());
export const getDataroomUploadCredentials = (sellerId, signal) => dataroomLifecycleGet(sellerId, '/credentials', signal);
export const getDataroomDocumentStatus = (sellerId, cursor, signal) => dataroomLifecycleGet(sellerId, `/items${cursor ? `?after=${encodeURIComponent(cursor)}` : ''}`, signal);
export const getDataroomItem = (sellerId, itemId, signal) => dataroomLifecycleGet(sellerId, `/items/${encodeURIComponent(itemId)}`, signal);
export const commandDataroomItem = (sellerId, itemId, payload) => post(`/admin/sellers/${encodeURIComponent(sellerId)}/dataroom_lifecycle/items/${encodeURIComponent(itemId)}/commands`, JSON.stringify(payload), true, taskSkillSessionHeaders());
export const getDataroomFolders = (sellerId, cursor, signal) => dataroomLifecycleGet(sellerId, `/items?kind=folder${cursor ? `&after=${encodeURIComponent(cursor)}` : ''}`, signal);
export const reviewDataroomPlacement = (sellerId, itemId, payload) => post(`/admin/sellers/${encodeURIComponent(sellerId)}/dataroom_lifecycle/items/${encodeURIComponent(itemId)}/review_placement`, JSON.stringify(payload), true, taskSkillSessionHeaders());
export const retryDataroomStage = (sellerId, itemId, payload) => post(`/admin/sellers/${encodeURIComponent(sellerId)}/dataroom_lifecycle/items/${encodeURIComponent(itemId)}/retry`, JSON.stringify(payload), true, taskSkillSessionHeaders());

export function sellerDeletionResources(sellerId) {
  return get(`/admin/sellers/${sellerId}/deletion_resources`);
}

function sellerShow(sellerId) {
  return get(`/admin/sellers/${sellerId}`);
}

function sellerGenerateSalesDeck(sellerId) {
  return post(`/admin/sellers/${sellerId}/generate_sales_deck`);
}

function sellerEnrichSeller(sellerId) {
  return post(`/admin/sellers/${sellerId}/enrich_seller`);
}

function sellerSendConfidentialityAgreement(sellerId) {
  return post(`/admin/sellers/${sellerId}/send_confidentiality_agreement`);
}

function sellerSendPreMeetingDiscoveryEmail(sellerId) {
  return post(`/admin/sellers/${sellerId}/send_pre_meeting_discovery_email`);
}

function sellerSendValuationRequestEmail(sellerId, payload={}) {
  return post(`/admin/sellers/${sellerId}/send_valuation_request_email`, JSON.stringify(payload));
}

function sellerSetupLlamaCloudIndex(sellerId) {
  return post(`/admin/sellers/${sellerId}/setup_llama_cloud_index`);
}

function sellerRagWebsite(sellerId) {
  return post(`/admin/sellers/${sellerId}/rag_website`);
}

function sellerRagStatus(sellerId) {
  return get(`/admin/sellers/${sellerId}/rag_store/status`);
}

function sellerRagSyncNow(sellerId) {
  return post(`/admin/sellers/${sellerId}/rag_store/sync`);
}

function sellerEnrichFromWebsite(website) {
  return post('/admin/sellers/enrich_from_website', JSON.stringify({ website }));
}

function sellerSyncGoogleDrive(sellerId) {
  return post(`/admin/sellers/${sellerId}/sync_google_drive`);
}

function sellerGenerateExitNavigator(sellerId) {
  return post(`/admin/sellers/${sellerId}/generate_exit_navigator`);
}

function driveFilesIndex(sellerId) {
  return get(`/admin/sellers/${sellerId}/drive_files`);
}

function buyerApprove(buyerId, dealId, payload) {
  return post(`/admin/buyers/${buyerId}/deals/${dealId}/dataroom_access_decision`, JSON.stringify(
    {
      deal: payload
    }
  ));
}

function buyerReject(buyerId, dealId, payload) {
  return post(`/admin/buyers/${buyerId}/deals/${dealId}/dataroom_access_decision`, JSON.stringify(
    {
      deal: payload
    }
  ));
}

function buyerUpdate(buyerId, payload) {
  return patch(`/admin/buyers/${buyerId}`, JSON.stringify(
    {
      buyer: payload
    }
  ));
}

function buyerCreate(data) {
  return post('/admin/buyers', JSON.stringify(data));
}

function sellerCreate(data) {
  return post('/admin/sellers', JSON.stringify(data));
}

function sellerUpdate(sellerId, data) {
  return patch(`/admin/sellers/${sellerId}`, JSON.stringify(data));
}

function sellerDelete(sellerId, options = {}) {
  return deleteRequest(`/admin/sellers/${sellerId}?${new URLSearchParams(options)}`);
}

function convertSellerToBuyer(sellerId) {
  return post(`/admin/sellers/${sellerId}/convert_to_buyer`, JSON.stringify({}));
}

function sellerChecklistUpdate(sellerId, data) {
  return patch(`/admin/sellers/${sellerId}/checklist`, JSON.stringify(data));
}

function taskUpdate(taskId, data) {
  return patch(`/admin/tasks/${taskId}`, JSON.stringify(data));
}

function sellerScrapeData(data) {
  return post('/sellers/scrape_data', JSON.stringify(data));
}

function sellerExitAuditCreate(data) {
  return post('/sellers', JSON.stringify(data));
}

function exitAuditShow(exitAuditId) {
  return get(`/exit_audits/${exitAuditId}`);
}

function sellerBuyers(sellerId, page = 1) {
  return get(`/admin/sellers/${sellerId}/buyers?page=${page}`);
}

function sellerBuyersAll(sellerId) {
  return get(`/admin/sellers/${sellerId}/buyers?all=true`);
}

function sellerDealStudioSync(sellerId, data) {
  return post(`/admin/sellers/${sellerId}/deal_studio_sync`, JSON.stringify(data));
}

function sellerDealroomAnalytics(sellerId, range = 'all') {
  return get(`/admin/sellers/${sellerId}/dealroom_analytics?range=${encodeURIComponent(range)}`);
}

function buyerDealroomAnalytics(buyerId, sellerId = null) {
  const query = sellerId ? `?seller_id=${sellerId}` : '';
  return get(`/admin/buyers/${buyerId}/dealroom_analytics${query}`);
}

function sellerBuyerOutreach(sellerId) {
  return get(`/admin/sellers/${sellerId}/buyer_outreach`);
}

function sellerBuyerOutreachUpdate(sellerId, data) {
  return patch(`/admin/sellers/${sellerId}/buyer_outreach`, JSON.stringify(data));
}

function sellerBuyerOutreachCreateRow(sellerId, data = {}) {
  return post(`/admin/sellers/${sellerId}/buyer_outreach/rows`, JSON.stringify(data));
}

function sellerBuyerOutreachUpdateRow(sellerId, rowId, data = {}) {
  return patch(`/admin/sellers/${sellerId}/buyer_outreach/rows/${rowId}`, JSON.stringify(data));
}

function sellerBuyerOutreachDeleteRow(sellerId, rowId, options = {}) {
  const query = options.dryRun ? '?dry_run=true' : '';
  return deleteRequest(`/admin/sellers/${sellerId}/buyer_outreach/rows/${rowId}${query}`);
}

function sellerBuyerOutreachBulkDeleteRows(sellerId, rowIds = []) {
  return deleteRequest(`/admin/sellers/${sellerId}/buyer_outreach/rows`, JSON.stringify({ row_ids: rowIds }));
}

function sellerBuyerOutreachBulkCreateRows(sellerId, rows = []) {
  return post(`/admin/sellers/${sellerId}/buyer_outreach/rows/bulk_create`, JSON.stringify({ rows }));
}

function sellerBuyerOutreachCreateColumn(sellerId, data = {}) {
  return post(`/admin/sellers/${sellerId}/buyer_outreach/columns`, JSON.stringify(data));
}

function sellerBuyerOutreachUpdateColumn(sellerId, columnId, data = {}) {
  return patch(`/admin/sellers/${sellerId}/buyer_outreach/columns/${columnId}`, JSON.stringify(data));
}

function sellerBuyerOutreachCreateActivity(sellerId, rowId, data = {}) {
  return post(`/admin/sellers/${sellerId}/buyer_outreach/rows/${rowId}/activities`, JSON.stringify(data));
}

function sellerBuyerOutreachImportCsv(sellerId, csv) {
  return post(`/admin/sellers/${sellerId}/buyer_outreach/import_csv`, JSON.stringify({ csv }));
}

function sellerBuyerOutreachExportCsv(sellerId) {
  return get(`/admin/sellers/${sellerId}/buyer_outreach/export_csv`);
}

function sellerBuyerOutreachStartResearchRun(sellerId) {
  return post(`/admin/sellers/${sellerId}/buyer_outreach/research_runs`, JSON.stringify({}));
}

function sellerBuyerOutreachLatestResearchRun(sellerId) {
  return get(`/admin/sellers/${sellerId}/buyer_outreach/research_runs/latest`);
}

function getAlfredDealDashboard(options = {}) {
  const params = new URLSearchParams();
  Object.entries(options).forEach(([key, value]) => {
    if (Array.isArray(value)) value.forEach(item => params.append(`${key}[]`, item));
    else if (value !== undefined && value !== null) params.set(key, String(value));
  });
  const query = params.toString() ? `?${params}` : '';
  return get(`/admin/alfred_dashboard${query}`, true, { 'X-Deal-Studio-Api-Key': DEAL_STUDIO_API_KEY });
}

function getAlfredSellers({ search = '', active } = {}) {
  const params = new URLSearchParams({
    ...(search ? { search } : {}),
    ...(active === undefined ? {} : { active: String(active) }),
  });
  return get(`/admin/alfred/sellers?${params.toString()}`, true);
}

// interactionsOffset pages the timeline. The dashboard used to take whatever
// the default limit gave it, which on a mandate with 104 threads was 50 and
// looked like the whole deal — the page rendered "4 months quiet" over a
// period that had plenty of correspondence in it.
function getAlfredSeller(sellerId, { interactionsOffset, interactionsLimit } = {}) {
  const params = new URLSearchParams({
    ...(interactionsOffset === undefined ? {} : { interactions_offset: String(interactionsOffset) }),
    ...(interactionsLimit === undefined ? {} : { interactions_limit: String(interactionsLimit) }),
  });
  const query = params.toString();
  return get(`/admin/alfred/sellers/${sellerId}${query ? `?${query}` : ''}`, true);
}

// Composed fresh on every click and never stored: a saved summary starts lying
// the moment the next email lands, and a stale one is worse than none.
function generateAlfredDealReport(sellerId) {
  return post(`/admin/alfred/sellers/${sellerId}/report`, null, true);
}

function getAlfredQuestion(id) {
  return get(`/admin/alfred/questions/${id}`, true);
}

function previewQuestionRelevance(questionIds, retryRunId) {
  return post('/admin/alfred/question_relevance_runs/preview', JSON.stringify({ ...(questionIds ? { question_ids: questionIds } : {}), retry_run_id: retryRunId }), true);
}
function checkQuestionRelevance(questionIds, selectionToken, retryRunId) {
  return post('/admin/alfred/question_relevance_runs', JSON.stringify({ ...(questionIds ? { question_ids: questionIds } : {}), selection_token: selectionToken, retry_run_id: retryRunId }), true);
}

function cancelQuestionRelevanceRun(id) {
  return post(`/admin/alfred/question_relevance_runs/${id}/cancel`, JSON.stringify({}), true);
}

function confirmQuestionRelevance(runId, questionIds, actionType = 'confirm') {
  return post(`/admin/alfred/question_relevance_runs/${runId}/confirm`, JSON.stringify({ question_ids: questionIds, action_type: actionType }), true);
}
function triageAlfredQuestion(id, actionType, updatedAt, ownerEmployeeId) {
  return post(`/admin/alfred/questions/${id}/triage`, JSON.stringify({ action_type: actionType, updated_at: updatedAt, owner_employee_id: ownerEmployeeId }), true);
}
function undoAlfredQuestionDigest(id) {
  return post(`/admin/alfred/question_digests/${id}/undo`, null, true);
}

function getQuestionRelevanceRun(id = 'latest') {
  return get(`/admin/alfred/question_relevance_runs/${id}`, true);
}

// Firm-wide agent question queue: every agent's questions, every owner.
// Supported filters: status, delivery_status, operational_state, question_lane,
// owner_employee_id, agent_id, stuck, and seller_id.
function getAgentQuestions(filters = {}) {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') params.set(key, value);
  });
  const query = params.toString();
  return get(`/admin/alfred/questions${query ? `?${query}` : ''}`, true);
}

function getAlfredOperations() {
  return get('/admin/alfred/operations', true);
}

function getAlfredOutbox() {
  return get('/admin/alfred/outbox', true);
}

function getAlfredSettings() {
  return get('/admin/alfred/settings', true);
}

function saveAlfredSettings(settings) {
  return patch('/admin/alfred/settings', JSON.stringify({ settings }), true);
}

function getAlfredTaskSettings() {
  return get('/admin/alfred/task_settings', true);
}

export function getAssistantRuns(kind, beforeId = null) {
  const resource = kind === 'drive' ? 'drive_organizer' : 'task_settings';
  const query = beforeId ? `?before_id=${encodeURIComponent(beforeId)}` : '';
  return get(`/admin/alfred/${resource}/runs${query}`, true);
}

function saveAlfredTaskSetting(taskSetting) {
  return patch('/admin/alfred/task_settings', JSON.stringify({ task_setting: taskSetting }), true);
}

function getDriveOrganizer(runId = null) {
  const query = runId ? `?run_id=${encodeURIComponent(runId)}` : '';
  return get(`/admin/alfred/drive_organizer${query}`, true);
}

export async function getDriveOrganizerActivity(runId, cursor, signal) {
  const query = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
  const opts = await formatHeaders(true, 'GET');
  return fetch(`${BACKEND_URL}/admin/alfred/drive_organizer/runs/${encodeURIComponent(runId)}/activity${query}`, { ...opts, signal });
}

export async function getDriveOrganizerRun(runId, signal) {
  const opts = await formatHeaders(true, 'GET');
  return fetch(`${BACKEND_URL}/admin/alfred/drive_organizer/runs/${encodeURIComponent(runId)}`, { ...opts, signal });
}

function saveDriveOrganizerSetting(setting) {
  return patch('/admin/alfred/drive_organizer', JSON.stringify({ setting }), true);
}

function runDriveOrganizer(sellerId) {
  return post('/admin/alfred/drive_organizer/run', JSON.stringify({ seller_id: sellerId }), true);
}

function messageDriveOrganizerRun(runId, message) {
  return post(`/admin/alfred/drive_organizer/runs/${runId}/messages`, JSON.stringify({ message }), true);
}

function approveDriveOrganizerRun(runId, planDigest) {
  return post(`/admin/alfred/drive_organizer/runs/${runId}/approve`, JSON.stringify({ plan_digest: planDigest }), true);
}

function rejectDriveOrganizerRun(runId, planDigest, reason, rule = null) {
  return post(
    `/admin/alfred/drive_organizer/runs/${runId}/reject`,
    JSON.stringify({ plan_digest: planDigest, reason, ...(rule ? { rule } : {}) }),
    true
  );
}

// Every colleague's Slack conversation with Alfred, read the way Slack shows
// it. Developer-only on the API side.
function getAlfredConversationPeople(filters = {}) {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') params.set(key, value);
  });
  const query = params.toString();
  return get(`/admin/alfred/conversations${query ? `?${query}` : ''}`, true);
}

function getAlfredConversationTimeline(employeeId, filters = {}) {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') params.set(key, value);
  });
  const query = params.toString();
  return get(`/admin/alfred/conversations/${employeeId}${query ? `?${query}` : ''}`, true);
}

function getAlfredWorkItems(filters = {}) {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') params.set(key, value);
  });
  const query = params.toString();
  return get(`/admin/alfred/work_items${query ? `?${query}` : ''}`, true);
}

function getAlfredWorkItem(id) {
  return get(`/admin/alfred/work_items/${id}`, true);
}

function getAlfredCrmBatches(page = 1) {
  return get(`/admin/alfred/crm_batches?page=${page}`, true);
}
function previewAlfredCrmBatch(criteria) {
  return post('/admin/alfred/crm_batches/preview', JSON.stringify(criteria), true);
}
function startAlfredCrmBatch(token) {
  return post('/admin/alfred/crm_batches', JSON.stringify({ token }), true);
}
function prepareAlfredCrmReplacement(id) {
  return post(`/admin/alfred/work_items/${id}/prepare_replacement`, null, true);
}

function retryAlfredCrmExecution(id) {
  return post(`/admin/alfred/work_items/${id}/retry_execution`, null, true);
}

function retryAlfredCrmBatch(id) {
  return post(`/admin/alfred/crm_batches/${id}/retry_failed`, null, true);
}
function restoreAlfredCrmBatchEntry(id, entryId) {
  return post(`/admin/alfred/crm_batches/${id}/restore`, JSON.stringify({ entry_id: entryId }), true);
}

function getCrmAmendmentSchema() {
  return get('/admin/alfred/work_items/amendment_schema', true);
}

function amendAlfredWorkItem(id, amendment) {
  return post(`/admin/alfred/work_items/${id}/amend`, JSON.stringify(amendment), true);
}

function decideAlfredWorkItem(id, decision, reason) {
  return post(
    `/admin/alfred/work_items/${id}/decide`,
    JSON.stringify({ decision, ...(reason ? { reason } : {}) }),
    true
  );
}

function askAlfredWorkItemNow(id, { deliveryChannelId = null, deliveryRecipientMode = null } = {}) {
  return post(
    `/admin/alfred/work_items/${id}/ask`,
    JSON.stringify({
      deliver_now: true,
      delivery_channel_id: deliveryChannelId,
      ...(deliveryRecipientMode ? { delivery_recipient_mode: deliveryRecipientMode } : {}),
    }),
    true
  );
}

function dismissAlfredWorkItem(id) {
  return post(`/admin/alfred/work_items/${id}/dismiss`, null, true);
}

function reassignAlfredWorkItem(id, ownerEmployeeId) {
  return patch(
    `/admin/alfred/work_items/${id}/reassign`,
    JSON.stringify({ owner_employee_id: ownerEmployeeId }),
    true
  );
}

function previewAlfredWorkItemRecipientRepair(id) {
  return get(`/admin/alfred/work_items/${id}/recipient_repair`, true);
}

function routeAlfredWorkItemQuestion(id) {
  return post(
    `/admin/alfred/work_items/${id}/route_question`,
    JSON.stringify({ confirmed: true }),
    true
  );
}

function sendAlfredWorkItemFollowUpNow(id) {
  return post(`/admin/alfred/work_items/${id}/send_follow_up`, null, true);
}

function createAgentQuestionDigest({
  questionIds,
  ownerEmployeeId,
  deliveryChannelId = null,
  scheduledFor = null,
  deliveryRecipientMode = null,
  message = null,
  draftVersion = null,
}) {
  return post(
    '/admin/alfred/question_digests',
    JSON.stringify({
      question_ids: questionIds,
      owner_employee_id: ownerEmployeeId,
      delivery_channel_id: deliveryChannelId,
      scheduled_for: scheduledFor,
      delivery_recipient_mode: deliveryRecipientMode,
      message,
      draft_version: draftVersion,
    }),
    true
  );
}

function createQuestionTestDelivery({questionIds, requestKey, voicedBody = null, message = null, draftVersion = null}) {
  return post('/admin/alfred/question_test_deliveries', JSON.stringify({question_ids: questionIds, request_key: requestKey, approval_enabled: true, voiced_body: voicedBody, message, draft_version: draftVersion}), true);
}
function getQuestionTestDelivery(id) {
  return get(`/admin/alfred/question_test_deliveries/${id}`, true);
}
function getQuestionTestDeliveries() {
  return get('/admin/alfred/question_test_deliveries', true);
}

function draftAgentQuestionDigest(questionIds) {
  return post('/admin/alfred/question_digests/draft', JSON.stringify({ question_ids: questionIds }), true);
}
function previewAgentQuestionDigest(questionIds) {
  const query = new URLSearchParams();
  questionIds.forEach(id => query.append('question_ids[]', id));
  return get(`/admin/alfred/question_digests/preview?${query}`, true);
}
function saveAgentQuestionDigestDraft(questionIds, message, version) {
  return patch('/admin/alfred/question_digests/save_draft', JSON.stringify({ question_ids: questionIds, message, version }), true);
}

function getAlfredQuestionDigests() {
  return get('/admin/alfred/question_digests', true);
}

function getAlfredRouteChoices(filters = {}) {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') params.set(key, value);
  });
  const query = params.toString();
  return get(`/admin/alfred/conversation_route_choices${query ? `?${query}` : ''}`, true);
}

function resolveAlfredRouteChoice(id, selectedConversationId) {
  return patch(
    `/admin/alfred/conversation_route_choices/${id}`,
    JSON.stringify({ selected_conversation_id: selectedConversationId }),
    true
  );
}

function controlAlfredConversationRun(id, action, reason) {
  return patch(
    `/admin/alfred/conversation_runs/${id}`,
    JSON.stringify({ control: action, ...(reason ? { reason } : {}) }),
    true
  );
}

function answerAlfredQuestion(id, body) {
  return post(`/admin/alfred/questions/${id}/messages`, JSON.stringify({ body }), true);
}

// A developer replying on the intended recipient's behalf: judged by Alfred
// like that person's own Slack reply, recorded under the developer's name.
function replyToAlfredQuestionAsOwner(id, body) {
  return post(`/admin/alfred/questions/${id}/proxy_reply`, JSON.stringify({ body }), true);
}

function dismissAlfredQuestion(id, { unsentOnly = false, updatedAt = null } = {}) {
  const payload = { ...(unsentOnly ? { unsent_only: true } : {}), ...(updatedAt ? { updated_at: updatedAt } : {}) };
  return post(`/admin/alfred/questions/${id}/dismiss`, Object.keys(payload).length ? JSON.stringify(payload) : null, true);
}

function reconcileAlfredQuestionClosure(id, closureAction = 'reconcile') {
  return post(`/admin/alfred/questions/${id}/reconcile_closure`, JSON.stringify({closure_action: closureAction}), true);
}

function reopenAlfredQuestion(id, { updatedAt = null } = {}) {
  return post(`/admin/alfred/questions/${id}/reopen`, updatedAt ? JSON.stringify({updated_at: updatedAt}) : null, true);
}

function retryAlfredQuestion(id) {
  return post(`/admin/alfred/questions/${id}/retry`, null, true);
}

function deliverAgentQuestion(id, {
  ownerEmployeeId = null,
  deliveryChannelId = null,
  deliveryRecipientMode = null,
} = {}) {
  const payload = {
    ...(ownerEmployeeId ? { owner_employee_id: ownerEmployeeId } : {}),
    delivery_channel_id: deliveryChannelId || null,
    ...(deliveryRecipientMode ? { delivery_recipient_mode: deliveryRecipientMode } : {}),
  };
  return post(`/admin/alfred/questions/${id}/deliver`, JSON.stringify(payload), true);
}

function recallAgentQuestionDelivery(id) {
  return post(`/admin/alfred/questions/${id}/recall_delivery`, null, true);
}

// ASK ON MY BEHALF — human-initiated questions. The partner writes the intent,
// Alfred voices the actual sentence, the partner approves the draft, and the
// question is delivered and answered like any other agent question. The
// requester is always the session partner — never sent in the payload.

function createAsk(payload) {
  return post('/admin/alfred/questions/asks', JSON.stringify(payload), true);
}

// The Conversations composer already contains the final sentence. This sends
// it verbatim as Alfred and uses the ordinary ask/thread/handoff lifecycle.
export function messageAlfredFromDashboard(employeeId, payload) {
  return post(`/admin/alfred/conversations/${employeeId}/messages`, JSON.stringify(payload), true);
}

function sendAlfredConversationMessage(payload) {
  return post('/admin/alfred/questions/send_as_alfred', JSON.stringify(payload), true);
}

function getAskOptions() {
  return get('/admin/alfred/questions/ask_options', true);
}

function getAskReadiness() {
  return get('/admin/alfred/questions/readiness', true);
}

function approveAsk(id, urgent) {
  return post(`/admin/alfred/questions/${id}/approve`, JSON.stringify({ urgent: Boolean(urgent) }), true);
}

function draftAlfredQuestion(id) {
  return post(`/admin/alfred/questions/${id}/draft_message`, null, true);
}

function draftAlfredWorkItem(id) {
  return post(`/admin/alfred/work_items/${id}/draft_message`, null, true);
}

function reviseAsk(id, text) {
  return post(`/admin/alfred/questions/${id}/revise`, JSON.stringify({ text }), true);
}

function revoiceAsk(id) {
  return post(`/admin/alfred/questions/${id}/revoice`, null, true);
}

function closeAsk(id, summary) {
  return post(
    `/admin/alfred/questions/${id}/close_ask`,
    JSON.stringify(summary ? { resolution_summary: summary } : {}),
    true
  );
}

// Human review queue for Brain proposals (Build Plan 6 Phase B). Defaults to
// the pending queue when no status is given, matching the controller.
function getBrainProposals(status) {
  const params = new URLSearchParams(status ? { status } : {});
  const query = params.toString();
  return get(`/admin/brain_proposals${query ? `?${query}` : ''}`, true);
}

// Developer-only, read-only projection of the allowlisted private Alfred Brain
// GitHub vaults. The graph is lightweight; note Markdown is fetched separately
// only after a developer selects a node.
function getAlfredBrain(source = 'alfred', refresh = false) {
  const params = new URLSearchParams({ source });
  if (refresh) params.set('refresh', 'true');
  return get(`/admin/alfred/brain?${params.toString()}`, true);
}

function getAlfredBrainNote(path, source = 'alfred') {
  const query = new URLSearchParams({ path: path || '', source }).toString();
  return get(`/admin/alfred/brain/note?${query}`, true);
}

function syncAlfredBrainFromNotion(source = 'alfred') {
  return post('/admin/alfred/brain/sync_notion', JSON.stringify({ source }), true);
}

function getBrainChat(source = 'alfred', chatId = null) {
  const params = new URLSearchParams({ source });
  if (chatId) params.set('chat_id', chatId);
  const query = params.toString();
  return get(`/admin/brain_chat?${query}`, true);
}

// Employee task chats additionally verify the real Stytch session. Harness
// credentials and executor URLs never enter the browser.
export async function taskSkillRequest(path, { method = 'GET', body, signal } = {}) {
  const multipart = typeof FormData !== 'undefined' && body instanceof FormData;
  const options = await formatHeaders(true, method, body == null ? null : (multipart ? body : JSON.stringify(body)));
  Object.assign(options.headers, taskSkillSessionHeaders());
  if (multipart) delete options.headers['Content-Type'];
  const response = await fetch(`${BACKEND_URL}/admin/${path}`, { ...options, signal });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    const error = new Error(payload.error || 'Could not update this Alfred task. Try again.');
    error.status = response.status;
    error.payload = payload;
    throw error;
  }
  return response.json();
}

export async function newsletterRequest(path = '', { method = 'GET', body, signal } = {}) {
  const session = taskSkillSessionHeaders()
  const options = await formatHeaders(true, method, body == null ? null : JSON.stringify(body))
  Object.assign(options.headers, session)
  const response = await fetch(`${BACKEND_URL}/admin/newsletters${path}`, { ...options, signal })
  const payload = await response.json().catch(() => ({}))
  if (!response.ok) {
    const error = new Error(payload.error || 'Could not update this newsletter. Try again.')
    error.status = response.status
    throw error
  }
  return payload
}

async function profileMatchRequest(resource, fallback, path, { method = 'GET', body, signal } = {}) {
  const session = taskSkillSessionHeaders()
  const options = await formatHeaders(true, method, body == null ? null : JSON.stringify(body))
  Object.assign(options.headers, session)
  const response = await fetch(`${BACKEND_URL}/admin/${resource}${path}`, { ...options, signal })
  const payload = await response.json().catch(() => ({}))
  if (!response.ok) {
    const error = new Error(payload.error || fallback)
    error.status = response.status
    error.payload = payload
    throw error
  }
  return payload
}

export function mandateMatchRequest(path = '', options = {}) {
  return profileMatchRequest('mandate_matches', 'Could not load this match. Please try again.', path, options)
}

export function cimIndexRequest(path = '', options = {}) {
  return profileMatchRequest('cim_indexes', 'Could not load the CIM. Please try again.', path, options)
}

export function anonymousProfileRequest(path = '', options = {}) {
  return profileMatchRequest('anonymous_profiles', 'Could not load profiles. Please try again.', path, options)
}

export function uploadCampaignImage(sellerId, file) {
  const body = new FormData()
  body.append('file',file)
  return taskSkillRequest(`sellers/${encodeURIComponent(sellerId)}/campaigns/upload_image`,{method:'POST',body})
}

export function uploadNewsletterImage(file) {
  const body = new FormData()
  body.append('file',file)
  return taskSkillRequest('newsletters/upload_image',{method:'POST',body})
}

export const getMyWorkspaceActiveMeetings = () => taskSkillRequest('my_workspace/meetings/active');
export const getMyWorkspaceScratchPad = id => taskSkillRequest(`my_workspace/meetings/${encodeURIComponent(id)}/scratch_pad`);
export const saveMyWorkspaceScratchPad = (id, body, version) => taskSkillRequest(`my_workspace/meetings/${encodeURIComponent(id)}/scratch_pad`, { method: 'PATCH', body: { body, version } });

export async function downloadTaskSkillArtifact(artifact) {
  const options = await formatHeaders(true, 'GET');
  Object.assign(options.headers, taskSkillSessionHeaders());
  const response = await fetch(`${BACKEND_URL}/admin/task_skill_artifacts/${encodeURIComponent(artifact.id)}/download`, options);
  if (!response.ok) throw new Error('Could not download this file.');
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement('a');
  link.href = url;
  link.download = artifact.filename;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function getBrainChatHistory(source = 'alfred') {
  const query = new URLSearchParams({ source }).toString();
  return get(`/admin/brain_chat/history?${query}`, true);
}

function resetBrainChat(source = 'alfred') {
  return post('/admin/brain_chat/reset', JSON.stringify({ source }), true);
}

function sendBrainChatMessage({ source = 'alfred', prompt, context = null, chatId = null }) {
  return post(
    '/admin/brain_chat/messages',
    JSON.stringify({ source, prompt, ...(context ? { context } : {}), ...(chatId ? { chat_id: chatId } : {}) }),
    true
  );
}

// Firm-wide interaction activity feed (developer-only). Filters pass through
// verbatim: kind, direction, seller_id, contact_id, from, to, limit.
function getAlfredActivity(filters = {}) {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') params.set(key, value);
  });
  const query = params.toString();
  return get(`/admin/alfred/activity${query ? `?${query}` : ''}`, true);
}

function approveBrainProposal(id) {
  return post(`/admin/brain_proposals/${id}/approve`, null, true);
}

function rejectBrainProposal(id) {
  return post(`/admin/brain_proposals/${id}/reject`, null, true);
}

function updateAlfredScorecardCell(data = {}) {
  return patch(
    '/admin/alfred_dashboard/scorecard_cells',
    JSON.stringify({ scorecard_cell: data }),
    true,
    { 'X-Deal-Studio-Api-Key': DEAL_STUDIO_API_KEY }
  );
}

function updateAlfredSellerEosFact(sellerId, field, data = {}) {
  return patch(
    `/admin/alfred_dashboard/sellers/${sellerId}/eos_facts/${field}`,
    JSON.stringify(data),
    true,
    { 'X-Deal-Studio-Api-Key': DEAL_STUDIO_API_KEY }
  );
}

function updateAlfredSeller(sellerId, data = {}) {
  return patch(
    `/admin/alfred_dashboard/sellers/${sellerId}`,
    JSON.stringify({ seller: data }),
    true,
    { 'X-Deal-Studio-Api-Key': DEAL_STUDIO_API_KEY }
  );
}

function updateAlfredSellerPayouts(sellerId, role, data = {}) {
  return patch(
    `/admin/alfred_dashboard/sellers/${sellerId}/payouts/${role}`,
    JSON.stringify({ payout: data }),
    true,
    { 'X-Deal-Studio-Api-Key': DEAL_STUDIO_API_KEY }
  );
}

function dealPodsIndex() {
  return get('/admin/deal_pods', true);
}

function dealPodCreate(data = {}) {
  return post('/admin/deal_pods', JSON.stringify({ deal_pod: data }), true);
}

function dealPodUpdate(dealPodId, data = {}) {
  return patch(`/admin/deal_pods/${dealPodId}`, JSON.stringify({ deal_pod: data }), true);
}

function dataroomUpdate(sellerId, data) {
  return patch(`/admin/sellers/${sellerId}/dataroom`, JSON.stringify(data));
}

// DEALS

function ndaTransportHeaders() {
  try { return taskSkillSessionHeaders(); } catch (_) { return {}; }
}

function dealsIndex(query=null) {
  return get(`/admin/deals?${query}`, false, new URLSearchParams(query).get('filter') === 'nda_review' ? ndaTransportHeaders() : {});
}

function dealUpdate(dealId, data) {
  return patch(`/admin/deals/${dealId}`, JSON.stringify(data), false, ndaTransportHeaders());
}

function dealChecklistUpdate(dealId, data) {
  return patch(`/admin/deals/${dealId}/checklist`, JSON.stringify(data));
}

// EXIT NAVIGATOR

function exitNavigatorUpdate(sellerId, data) {
  return patch(`/admin/sellers/${sellerId}/exit_navigator`, JSON.stringify(data));
}

// BREAKWATER DRIVE

function breakwaterDriveUpdate(sellerId, data) {
  return patch(`/admin/sellers/${sellerId}/breakwater_drive`, JSON.stringify(data));
}

// RAG STORE

function ragStoreUpdate(sellerId, data) {
  return patch(`/admin/sellers/${sellerId}/rag_store`, JSON.stringify(data));
}

// AUTH

function stytchAuth(memberId, organizationId) {
  return post('/admin/auth/stytch', JSON.stringify({ 
    member_id: memberId,
    organization_id: organizationId
  }));
}

function localDashboardAuth(email) {
  return post('/admin/auth/local_dashboard', JSON.stringify({ email }));
}

// EMPLOYEE

function employeesIndex() {
  return get('/admin/employees', true);
}

function employeeCreate(data = {}) {
  return post('/admin/employees', JSON.stringify({ employee: data }), true);
}

function showEmployee(memberId) {
  return get(`/admin/employees/show?member_id=${memberId}`, true);
}

function employeeUpdate(employeeId, data = {}) {
  return patch(`/admin/employees/${employeeId}`, JSON.stringify({ employee: data }), true);
}

function employeeProfileShow(employeeId) {
  return get(`/admin/employees/${employeeId}`, true);
}

function employeeProfileUpdate(employeeId, data = {}) {
  return patch(`/admin/employees/${employeeId}`, JSON.stringify({ employee: data }), true);
}

export async function employeeProfilePhotoUpload(employeeId, file) {
  const body = new FormData();
  body.append('file', file);
  const options = await formatHeaders(true, 'POST', body);
  delete options.headers['Content-Type'];
  return fetch(`${BACKEND_URL}/admin/employees/${encodeURIComponent(employeeId)}/profile_photo`, options);
}

function employeeDeals(employeeId, visibility = 'active') {
  return get(`/admin/employees/${employeeId}/deals?visibility=${encodeURIComponent(visibility)}`, true);
}

function updatePartnerSettings(memberId, viewFullPipeline) {
  return patch('/admin/employees/partner_settings', JSON.stringify({
    member_id: memberId,
    view_full_pipeline: viewFullPipeline
  }), true);
}

// EXIT NAVIGATOR

function exitNavigatorGenerate(sellerId) {
  return post(`/admin/sellers/${sellerId}/exit_navigator/generate`);
}

// PARTNERS

function partnersIndex(query=null) {
  return get(`/admin/partners${query ? `?${query}` : ''}`, true);
}

function partnerCreate(data) {
  return post('/admin/partners', JSON.stringify({ partner: data }), true);
}

function partnerUpdate(partnerId, data) {
  return patch(`/admin/partners/${partnerId}`, JSON.stringify({ partner: data }), true);
}

function partnerDestroy(partnerId) {
  return deleteRequest(`/admin/partners/${partnerId}`, true);
}

// CONTACT SOURCES

function contactSourcesIndex(query=null) {
  return get(`/admin/contact_sources${query ? `?${query}` : ''}`, true);
}

function contactSourceCreate(data) {
  return post('/admin/contact_sources', JSON.stringify({ contact_source: data }), true);
}

function contactSourceUpdate(contactSourceId, data) {
  return patch(`/admin/contact_sources/${contactSourceId}`, JSON.stringify({ contact_source: data }), true);
}

function contactSourceDestroy(contactSourceId) {
  return deleteRequest(`/admin/contact_sources/${contactSourceId}`, true);
}

// LEAD ACQUISITION

function leadEntryPointsIndex(query=null) {
  return get(`/admin/lead_entry_points?version=2${query ? `&${query}` : ''}`, true);
}

function leadEntryPointCreate(data) {
  return post('/admin/lead_entry_points', JSON.stringify({ lead_entry_point: data }), true);
}

function leadEntryPointUpdate(leadEntryPointId, data) {
  return patch(`/admin/lead_entry_points/${leadEntryPointId}`, JSON.stringify({ lead_entry_point: data }), true);
}

function leadEntryPointDestroy(leadEntryPointId) {
  return deleteRequest(`/admin/lead_entry_points/${leadEntryPointId}`, true);
}

function contactsIndex(query=null) {
  return get(`/admin/contacts${query ? `?${query}` : ''}`, true);
}

function contactCreate(data) {
  return post('/admin/contacts', JSON.stringify({ contact: data }), true);
}

function referralPartnersIndex(query=null) {
  return get(`/admin/referral_partners${query ? `?${query}` : ''}`, true);
}

function referrersIndex(query=null) {
  return get(`/admin/referrers${query ? `?${query}` : ''}`, true);
}

function referralPartnerCreate(data) {
  return post('/admin/referral_partners', JSON.stringify({ referral_partner: data }), true);
}

function referralPartnerAddContact(referralPartnerId, contactId, primary=false) {
  return post(`/admin/referral_partners/${referralPartnerId}/add_contact`, JSON.stringify({
    contact_id: contactId,
    primary
  }), true);
}

// TASKS PAGE (public, no auth)

function findDealPartnerByEmail(email) {
  return get(`/admin/deal_partners/find_by_email?email=${encodeURIComponent(email)}`);
}

function getMyTasks(dealPartnerId, sellerId = null) {
  const qs = sellerId ? `?deal_partner_id=${dealPartnerId}&seller_id=${sellerId}` : `?deal_partner_id=${dealPartnerId}`;
  return get(`/admin/tasks/my_tasks${qs}`);
}

function reassignTask(taskId, assignedDealPartnerId, notes) {
  const body = { assigned_deal_partner_id: assignedDealPartnerId };
  if (notes !== undefined) body.notes = notes;
  return patch(`/admin/tasks/${taskId}/reassign`, JSON.stringify(body));
}

function updateTaskField(taskId, data) {
  return patch(`/admin/tasks/${taskId}`, JSON.stringify(data));
}

function taskCompleteWithHandler(taskId, handler, payload) {
  return post(`/admin/tasks/${taskId}/complete_with_handler`, JSON.stringify({ handler, payload }));
}

// EMAIL COMPOSE

function previewEmail(emailType, sellerId, options = {}) {
  return post('/admin/emails/preview', JSON.stringify({
    email_type: emailType,
    seller_id: sellerId,
    deal_id: options.dealId,
    meeting_date: options.meetingDate,
    meeting_time: options.meetingTime
  }));
}

function sendEmail(emailType, sellerId, options = {}) {
  const body = {
    email_type: emailType,
    seller_id: sellerId,
    deal_id: options.dealId,
    meeting_date: options.meetingDate,
    meeting_time: options.meetingTime
  };
  if (options.attachments && options.attachments.length > 0) body.attachments = options.attachments;
  if (options.cc && options.cc.length > 0) body.cc = options.cc;
  if (options.bcc && options.bcc.length > 0) body.bcc = options.bcc;
  if (options.body !== undefined) body.body = options.body;
  if (options.bodyHtml !== undefined) body.body_html = options.bodyHtml;
  if (options.templateVariables) body.template_variables = options.templateVariables;
  if (options.subject) body.subject = options.subject;
  if (options.threadId) body.thread_id = options.threadId;
  return post('/admin/emails/send_email', JSON.stringify(body));
}

function createEmailUploadUrl() {
  return post('/admin/emails/create_upload_url', null);
}

async function uploadEmailAttachment(uploadUrl, file) {
  // The signed upload URL accepts multipart directly. Don't use the standard `post()`
  // helper because it sends JSON; this needs FormData.
  const fullUrl = uploadUrl.startsWith('http') ? uploadUrl : `${BACKEND_URL}${uploadUrl}`;
  const formData = new FormData();
  formData.append('file', file);
  // The signed URL is the auth — no extra headers needed.
  return fetch(fullUrl, { method: 'POST', body: formData });
}

// Generic file upload pipeline (used by file_upload handler).
function createGenericUploadUrl() {
  return post('/admin/uploads/create_upload_url', null);
}

async function uploadGenericFile(uploadUrl, file) {
  const fullUrl = uploadUrl.startsWith('http') ? uploadUrl : `${BACKEND_URL}${uploadUrl}`;
  const formData = new FormData();
  formData.append('file', file);
  return fetch(fullUrl, { method: 'POST', body: formData });
}

function browseDriveFolder(sellerId, folderId, view = 'google_drive', options = {}) {
  const params = new URLSearchParams();
  if (folderId) params.set('folder_id', folderId);
  if (view) params.set('view', view);
  if (options.search) params.set('search', options.search);
  const qs = params.toString() ? `?${params.toString()}` : '';
  return get(`/admin/sellers/${sellerId}/drive_files/browse${qs}`);
}

function createDriveFolder(sellerId, folderId, name, options = {}) {
  const body = { folder_id: folderId, name };
  if (options.view) body.view = options.view;
  // Drag-and-drop folder chains use find_or_create so re-running a drop
  // (e.g. resuming locally-buffered files after a refresh) reuses folders
  // created earlier instead of duplicating them.
  if (options.findOrCreate) body.find_or_create = true;
  return post(`/admin/sellers/${sellerId}/drive_files/create_folder`, JSON.stringify(body));
}

function setupSellerDrive(sellerId) {
  return post(`/admin/sellers/${sellerId}/drive_files/setup`);
}

function createSellerDealroom(sellerId) {
  return post(`/admin/sellers/${sellerId}/drive_files/create_dealroom`);
}

function getDriveFile(sellerId, fileId) {
  return get(`/admin/sellers/${sellerId}/drive_files/${encodeURIComponent(fileId)}`);
}

function deleteDriveItem(sellerId, itemId, payload) {
  return deleteRequest(
    `/admin/sellers/${sellerId}/drive_files/${encodeURIComponent(itemId)}`,
    payload ? JSON.stringify(payload) : null
  );
}

function removeLlamaCloudKnowledgeFile(sellerId, itemId) {
  return deleteRequest(`/admin/sellers/${sellerId}/drive_files/${encodeURIComponent(itemId)}/llama_cloud`);
}

function updateDriveItemSyncSettings(sellerId, itemId, payload) {
  return patch(`/admin/sellers/${sellerId}/drive_files/${encodeURIComponent(itemId)}/sync_settings`, JSON.stringify(payload));
}

function renameDealroomItem(sellerId, itemId, payload) {
  return patch(`/admin/sellers/${sellerId}/drive_files/${encodeURIComponent(itemId)}/rename`, JSON.stringify(payload));
}

function moveDriveItem(sellerId, itemId, payload) {
  return patch(`/admin/sellers/${sellerId}/drive_files/${encodeURIComponent(itemId)}/move`, JSON.stringify(payload));
}

function retryDriveItemSync(sellerId, itemId, target) {
  return post(`/admin/sellers/${sellerId}/drive_files/${encodeURIComponent(itemId)}/retry_sync`, JSON.stringify({ target }));
}

async function uploadDriveFile(sellerId, folderId, file) {
  const url = `${BACKEND_URL}/admin/sellers/${sellerId}/drive_files/upload`;
  const formData = new FormData();
  formData.append('folder_id', folderId);
  formData.append('file', file);
  const options = await formatHeaders(false, 'POST');
  delete options.headers['Content-Type'];
  options.body = formData;
  return fetch(url, options);
}

// Durable drag-and-drop upload: stage one file's bytes on the server and let a
// background worker push it to its destination, so the upload survives a page
// refresh. `view` is 'google_drive' | 'dealroom'; `folderId` is the resolved
// target folder; `folderPath` (dealroom) recreates a dropped folder's
// structure; `batchId` groups the files of one drop for polling.
async function stageDriveUpload(sellerId, { file, view, folderId, folderPath, batchId }) {
  const url = `${BACKEND_URL}/admin/sellers/${sellerId}/drive_uploads`;
  const formData = new FormData();
  formData.append('file', file);
  formData.append('view', view);
  formData.append('batch_id', batchId);
  if (folderId) formData.append('folder_id', folderId);
  if (folderPath) formData.append('folder_path', folderPath);
  const options = await formatHeaders(false, 'POST');
  delete options.headers['Content-Type'];
  options.body = formData;
  return fetch(url, options);
}

// Poll staged uploads. Pass `batchId` to track a single drop, or `active: true`
// to list in-flight uploads across the seller (used to re-attach the progress
// UI to an ongoing batch after a page refresh).
function listDriveUploads(sellerId, { batchId, active } = {}) {
  const params = new URLSearchParams();
  if (batchId) params.set('batch_id', batchId);
  if (active) params.set('active', 'true');
  const qs = params.toString() ? `?${params.toString()}` : '';
  return get(`/admin/sellers/${sellerId}/drive_uploads${qs}`);
}

// Direct Dealroom upload (BW-343). Shares the `upload` endpoint with the
// Google Drive view — `view=dealroom` routes it to the Papermark dealroom.
// Multipart body; `folder_id` targets an existing dealroom folder and
// `folder_path` carries the dropped folder's relative path (e.g.
// "Financials/Q1") so the api can recreate structure.
async function uploadDealroomFile(sellerId, folderId, file, folderPath) {
  const url = `${BACKEND_URL}/admin/sellers/${sellerId}/drive_files/upload`;
  const formData = new FormData();
  formData.append('view', 'dealroom');
  if (folderId) formData.append('folder_id', folderId);
  if (folderPath) formData.append('folder_path', folderPath);
  formData.append('file', file);
  const options = await formatHeaders(false, 'POST');
  delete options.headers['Content-Type'];
  options.body = formData;
  return fetch(url, options);
}

// Replace the primary version of a Dealroom document (BW-345). Multipart body
// with the replacement file; the api only serves this action for the dealroom
// view, so `view=dealroom` is always sent. `document_id` carries the
// Papermark document id (the api resolves it from the dataroom structure
// when absent).
async function replaceDealroomFileVersion(sellerId, itemId, file, payload = {}) {
  const url = `${BACKEND_URL}/admin/sellers/${sellerId}/drive_files/${encodeURIComponent(itemId)}/replace_version`;
  const formData = new FormData();
  formData.append('view', 'dealroom');
  if (payload.document_id) formData.append('document_id', payload.document_id);
  formData.append('file', file);
  const options = await formatHeaders(false, 'POST');
  delete options.headers['Content-Type'];
  options.body = formData;
  return fetch(url, options);
}

async function uploadLlamaCloudKnowledgeFile(sellerId, file) {
  const url = `${BACKEND_URL}/admin/sellers/${sellerId}/drive_files/upload_llama_cloud`;
  const formData = new FormData();
  formData.append('file', file);
  const options = await formatHeaders(false, 'POST');
  delete options.headers['Content-Type'];
  options.body = formData;
  return fetch(url, options);
}

async function downloadDriveFile(sellerId, fileId, filename) {
  const url = `${BACKEND_URL}/admin/sellers/${sellerId}/drive_files/${encodeURIComponent(fileId)}/download`;
  const res = await fetch(url, await formatHeaders(false, 'GET'));
  if (!res.ok) throw new Error(`download failed: ${res.status}`);
  const blob = await res.blob();
  const objUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objUrl;
  a.download = filename || fileId;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(objUrl), 1000);
}

function previewSlackChannel(sellerId) {
  return get(`/admin/sellers/${sellerId}/slack_channel/preview`);
}

function getDealTemplate() {
  return get('/admin/sop/deal_template');
}

// Bulk complete or reopen a mix of Task + DealTask ids in a single request.
// ids: [{ id }, ...]
// completed: true to complete, false to reopen
// Atomically jump the funnel to a target task: bulk-complete or bulk-reopen
// the right side of the funnel AND advance/roll-back the seller/deal status,
// all in a single server-side transaction. direction: 'forward' | 'backward'.
function jumpToTask(targetTaskId, direction) {
  return post('/admin/tasks/jump_to', JSON.stringify({ target_task_id: targetTaskId, direction }));
}

function bulkCatchUpTasks(ids, completed, verificationEvidence = undefined, force = false) {
  const task_ids = ids.map(i => i.id);
  const body = { task_ids, completed };
  if (verificationEvidence !== undefined) body.verification_evidence = verificationEvidence;
  if (force) body.force = true;
  return patch('/admin/tasks/bulk_catch_up', JSON.stringify(body));
}

function getSellerDealPartners(dealPartnerId, { view } = {}) {
  const query = view ? `?view=${encodeURIComponent(view)}` : '';
  return get(`/admin/deal_partners/${dealPartnerId}/sellers_with_tasks${query}`);
}

function getMyMetrics(params = {}) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => { if (value !== '' && value != null) query.set(key, String(value)); });
  const suffix = query.toString();
  return get(`/admin/my_metrics${suffix ? `?${suffix}` : ''}`);
}

export function analyticsRequest(resource = '', filters = {}) {
  const allowed = ['', 'visits', 'sessions', 'documents', 'document-pages'];
  if (!allowed.includes(resource)) throw new Error('Unknown analytics report');
  const query = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => { if (value !== '' && value != null) query.set(key, String(value)); });
  return get(`/admin/analytics${resource ? `/${resource}` : ''}?${query.toString()}`);
}

function updateMyMetricsSeller(sellerId, data = {}) {
  return patch(`/admin/my_metrics/sellers/${sellerId}`, JSON.stringify({ seller: data }));
}

function updateMyMetricsSellerEosFact(sellerId, field, data = {}) {
  return patch(
    `/admin/my_metrics/sellers/${sellerId}/eos_facts/${field}`,
    JSON.stringify(data)
  );
}

function updateMyMetricsSellerPayouts(sellerId, role, data = {}) {
  return patch(
    `/admin/my_metrics/sellers/${sellerId}/payouts/${role}`,
    JSON.stringify({ payout: data })
  );
}

function dashboardSearch(query) {
  return get(`/admin/dashboard_search?q=${encodeURIComponent(query)}`);
}

function listAllDealPartners() {
  return get('/admin/deal_partners');
}

function getPriorityTasks(dealPartnerId) {
  return get(`/admin/tasks/priority?deal_partner_id=${dealPartnerId}`);
}

function getMyMeetingNotes(dealPartnerId, page = 1) {
  const query = new URLSearchParams({ page: String(page) });
  return get(`/admin/deal_partners/${dealPartnerId}/meeting_notes?${query.toString()}`);
}

function getMeetingNote(meetingNoteId) {
  return get(`/admin/meeting_notes/${meetingNoteId}`);
}

function updateMeetingNote(meetingNoteId, data) {
  return patch(`/admin/meeting_notes/${meetingNoteId}`, JSON.stringify({ meeting_note: data }));
}

function createMeetingRecordingPublicLink(meetingNoteId, access = {}) {
  return post(
    `/admin/meeting_notes/${encodeURIComponent(meetingNoteId)}/public_recording_link`,
    JSON.stringify(access)
  );
}

function shareMeetingNote(meetingNoteId, dealPartnerIds) {
  return post(`/admin/meeting_notes/${meetingNoteId}/share`, JSON.stringify({ deal_partner_ids: dealPartnerIds }));
}

function unshareMeetingNote(meetingNoteId, dealPartnerId) {
  return post(`/admin/meeting_notes/${meetingNoteId}/unshare`, JSON.stringify({ deal_partner_id: dealPartnerId }));
}

function getPublicMeetingRecording(token, unlockToken = null) {
  const headers = unlockToken ? { 'X-Recording-Unlock-Token': unlockToken } : {};
  return get(`/public/meeting_recordings/${encodeURIComponent(token)}`, false, headers);
}

function unlockPublicMeetingRecording(token, password) {
  return post(
    `/public/meeting_recordings/${encodeURIComponent(token)}/unlock`,
    JSON.stringify({ password })
  );
}

function updateMeetingNoteTask(taskId, data) {
  return patch(`/admin/meeting_note_tasks/${taskId}`, JSON.stringify({ meeting_note_task: data }));
}

function assignSellerDealPartners(requestingDealPartnerId, sellerId, assignments) {
  return patch(`/admin/deal_partners/${requestingDealPartnerId}/assign_to_seller`, JSON.stringify({
    seller_id: sellerId,
    assignments
  }));
}

function unassignDealPartnerFromSeller(dealPartnerId, sellerId) {
  return deleteRequest(`/admin/deal_partners/${dealPartnerId}/unassign_from_seller`, JSON.stringify({
    seller_id: sellerId
  }));
}

// GMAIL

function getGmailMessages(email, folder = 'inbox', pageToken = null) {
  let url = `/admin/gmail/messages?email=${encodeURIComponent(email)}&folder=${folder}`
  if (pageToken) url += `&page_token=${pageToken}`
  return get(url)
}

function getGmailThread(email, threadId) {
  return get(`/admin/gmail/threads/${threadId}?email=${encodeURIComponent(email)}`)
}

function searchGmailPriorThreads(email, contactEmail, { newerThanDays = 30, maxResults = 10 } = {}) {
  const params = new URLSearchParams({
    email,
    contact_email: contactEmail,
    newer_than_days: String(newerThanDays),
    max_results: String(maxResults),
  })
  return get(`/admin/gmail/threads?${params.toString()}`)
}

function getGmailSignature(email) {
  return get(`/admin/gmail/signature?email=${encodeURIComponent(email)}`);
}

function sendGmailReply(data) {
  return post('/admin/gmail/reply', JSON.stringify(data))
}

function deleteGmailMessage(email, messageId) {
  return deleteRequest(`/admin/gmail/messages/${messageId}?email=${encodeURIComponent(email)}`)
}

function markGmailThreadRead(email, threadId) {
  return post(`/admin/gmail/threads/${threadId}/read`, JSON.stringify({ email }))
}

// TEAM INBOX

export function getEmailCalibrationConfiguration() {
  return get('/admin/email_classifications/configuration')
}

export function getEmailClassifications(mailboxId, reviewStatus, page = 1) {
  const params = new URLSearchParams({ mailbox_id: mailboxId, review_status: reviewStatus, page: String(page) })
  return get(`/admin/email_classifications?${params}`)
}

export function getEmailClassification(id, mailboxId) {
  return get(`/admin/email_classifications/${encodeURIComponent(id)}?mailbox_id=${encodeURIComponent(mailboxId)}`)
}

export function saveEmailClassificationReview(id, mailboxId, review) {
  return post(`/admin/email_classifications/${encodeURIComponent(id)}/reviews`, JSON.stringify({ ...review, mailbox_id: mailboxId }))
}

export function retryEmailClassification(id, mailboxId) {
  return post(`/admin/email_classifications/${encodeURIComponent(id)}/retry`, JSON.stringify({ mailbox_id: mailboxId }))
}

function getTeamInboxSignature(mailbox) {
  return get(`/admin/team_inbox/signature?mailbox=${encodeURIComponent(mailbox)}`);
}

function getTeamInboxMailboxes() {
  return get('/admin/team_inbox/mailboxes')
}

function createTeamInboxMailbox(data) {
  return post('/admin/team_inbox/mailboxes', JSON.stringify(data))
}

function updateTeamInboxMailbox(mailboxId, data) {
  return patch(`/admin/team_inbox/mailboxes/${encodeURIComponent(mailboxId)}`, JSON.stringify(data))
}

function getTeamInboxThreads({ mailbox, folder = 'inbox', query = '', pageToken = null, maxResults = 25 } = {}) {
  const params = new URLSearchParams({ folder, max_results: String(maxResults) })
  if (mailbox) params.set('mailbox', mailbox)
  if (query) params.set('q', query)
  if (pageToken) params.set('page_token', pageToken)
  return get(`/admin/team_inbox/threads?${params.toString()}`)
}

function getTeamInboxThread(threadId, mailbox) {
  const params = new URLSearchParams()
  if (mailbox) params.set('mailbox', mailbox)
  return get(`/admin/team_inbox/threads/${encodeURIComponent(threadId)}?${params.toString()}`)
}

// My Workspace always reads the signed-in employee's own mailbox and calendar;
export function getMyWorkspaceEmailClassification(messageId) {
  return get(`/admin/my_workspace/inbox/messages/${encodeURIComponent(messageId)}/classification`)
}

export function classifyMyWorkspaceEmail(messageId) {
  return post(`/admin/my_workspace/inbox/messages/${encodeURIComponent(messageId)}/classification`)
}

export function saveMyWorkspaceEmailClassification(messageId, review) {
  return post(`/admin/my_workspace/inbox/messages/${encodeURIComponent(messageId)}/classification/reviews`, JSON.stringify(review))
}

export function retryMyWorkspaceEmailClassification(messageId) {
  return post(`/admin/my_workspace/inbox/messages/${encodeURIComponent(messageId)}/classification/retry`)
}

// the API resolves whose from the session, so no mailbox is sent.
export function searchMyWorkspaceMail(query) {
  return get(`/admin/my_workspace/search_suggestions?${new URLSearchParams({ q: query }).toString()}`)
}

export function searchMyWorkspaceRecipients(query) {
  return get(`/admin/my_workspace/recipients?${new URLSearchParams({ q: query }).toString()}`)
}

export function getMyWorkspaceInboxArrivals({ since, pageToken = null }) {
  return getMyWorkspaceInbox({ query: `in:inbox after:${Math.floor(since / 1000)}`, pageToken })
}

function getMyWorkspaceInbox({ folder = 'inbox', label = null, query = '', pageToken = null, categories = [], nowAction = null, maxResults = null } = {}) {
  if (folder === 'drafts' && !label && !query) return getMyWorkspaceDrafts(pageToken)
  const params = new URLSearchParams({ folder })
  categories.forEach(category => params.append('categories[]', category))
  if (nowAction) params.set('now_action', nowAction)
  if (maxResults !== null) params.set('max_results', maxResults)
  if (label) params.set('label', label)
  if (query) params.set('q', query)
  if (pageToken) params.set('page_token', pageToken)
  return get(`/admin/my_workspace/inbox?${params.toString()}`)
}

// group: 'active' (Active Clients) or 'seller_follow_ups' (all three follow-up statuses).
function getMyWorkspaceClients(group) {
  return get(`/admin/my_workspace/clients?${new URLSearchParams({ group }).toString()}`)
}

function getMyWorkspaceClientThreads(group, clientId, { pageToken = null } = {}) {
  const params = new URLSearchParams({ group })
  if (pageToken) params.set('page_token', pageToken)
  return get(`/admin/my_workspace/clients/${encodeURIComponent(clientId)}/threads?${params.toString()}`)
}

export function getWorkInboxPreferences() {
  return get('/admin/my_workspace/work_inbox_preferences')
}

export function saveWorkInboxPreferences(categories) {
  return patch('/admin/my_workspace/work_inbox_preferences', JSON.stringify({ categories }))
}

function getMyWorkspaceEmailRules() {
  return get('/admin/my_workspace/email_rules')
}

function saveMyWorkspaceEmailRules(data) {
  return patch('/admin/my_workspace/email_rules', JSON.stringify(data))
}

function getMyWorkspaceWritingStyle() {
  return get('/admin/my_workspace/writing_style')
}

function analyseMyWorkspaceWritingStyle(data) {
  return post('/admin/my_workspace/writing_style/analyse', JSON.stringify(data))
}

function getMyWorkspaceWritingStyleAnalysis() {
  return get('/admin/my_workspace/writing_style/analysis')
}

function saveMyWorkspaceWritingStyle(data) {
  return patch('/admin/my_workspace/writing_style', JSON.stringify(data))
}

function getMyWorkspaceLabels() {
  return get('/admin/my_workspace/inbox/labels')
}

export function startMyWorkspaceEmailDraft(data) {
  return post('/admin/my_workspace/email_drafts', JSON.stringify(data))
}

export function startMyWorkspaceEmailAnalysis(data) {
  return post('/admin/my_workspace/email_analyses', JSON.stringify(data))
}

export function getWorkspaceHermesPilot() {
  return get('/admin/my_workspace/hermes')
}

export function getWorkspaceHermesConversation(id) {
  return get(`/admin/my_workspace/hermes/${encodeURIComponent(id)}`)
}

export function sendWorkspaceHermesMessage(data) {
  return post('/admin/my_workspace/hermes', JSON.stringify(data))
}

export function getWorkspaceHermesResources() {
  return get('/admin/my_workspace/hermes/resources')
}

export function setWorkspaceHermesConnector(connectorId, enabled) {
  return patch(`/admin/my_workspace/hermes/connectors/${encodeURIComponent(connectorId)}`, JSON.stringify({enabled}))
}

export function createWorkspaceHermesConversation(id) {
  return post('/admin/my_workspace/hermes/conversations', JSON.stringify({ id }))
}

export async function uploadWorkspaceHermesAttachment(conversationId, file, id, relativePath) {
  const path = `/admin/my_workspace/hermes/${encodeURIComponent(conversationId)}/attachments`
  const body = new FormData()
  body.append('file', file)
  body.append('upload_id', id)
  body.append('relative_path', relativePath)
  const options = await formatHeaders(false, 'POST', body, {}, path)
  delete options.headers['Content-Type']
  return fetch(BACKEND_URL + path, options)
}

export function removeWorkspaceHermesAttachment(conversationId, id) {
  return deleteRequest(`/admin/my_workspace/hermes/${encodeURIComponent(conversationId)}/attachments/${encodeURIComponent(id)}`)
}

export function recoverWorkspaceHermesTurn(id, turnId) {
  return post(`/admin/my_workspace/hermes/${encodeURIComponent(id)}/recover`, JSON.stringify({ turn_id: turnId }))
}

export function getMyWorkspaceEmailRuns(threadId = null, { includeFinished = false } = {}) {
  const query = new URLSearchParams()
  if (threadId) query.set('thread_id', threadId)
  if (includeFinished) query.set('include_finished', 'true')
  return get(`/admin/my_workspace/email_runs${query.size ? `?${query}` : ''}`)
}

export function getMyWorkspaceLiveUpdates() {
  return get('/admin/my_workspace/live_updates')
}

export function getMyWorkspaceEmailRun(runId) {
  return get(`/admin/my_workspace/email_runs/${encodeURIComponent(runId)}`)
}

export function getMyWorkspaceComposerResources(runId) {
  return get(runId ? `/admin/my_workspace/email_runs/${encodeURIComponent(runId)}/resources` : '/admin/my_workspace/composer_resources')
}

export async function uploadMyWorkspaceMailAttachment(file) {
  const path = '/admin/my_workspace/inbox/attachments'
  const body = new FormData()
  body.append('file', file)
  const options = await formatHeaders(false, 'POST', body, {}, path)
  delete options.headers['Content-Type']
  return fetch(BACKEND_URL + path, options)
}

export function removeMyWorkspaceMailAttachment(token) {
  return deleteRequest(`/admin/my_workspace/inbox/attachments/${encodeURIComponent(token)}`)
}

export async function uploadMyWorkspaceChatAttachment(runId, file, id, relativePath) {
  const path = `/admin/my_workspace/email_runs/${encodeURIComponent(runId)}/attachments`
  const body = new FormData()
  body.append('file', file)
  body.append('upload_id', id)
  body.append('relative_path', relativePath)
  const options = await formatHeaders(false, 'POST', body, {}, path)
  delete options.headers['Content-Type'] // The browser supplies the multipart boundary.
  return fetch(BACKEND_URL + path, options)
}

export function removeMyWorkspaceChatAttachment(runId, id) {
  return deleteRequest(`/admin/my_workspace/email_runs/${encodeURIComponent(runId)}/attachments/${encodeURIComponent(id)}`)
}

export function startMyWorkspaceEmailChat(data) {
  return post('/admin/my_workspace/email_chats', JSON.stringify(data))
}

export function sendMyWorkspaceEmailQuestion(runId, data) {
  return post(`/admin/my_workspace/email_runs/${encodeURIComponent(runId)}/messages`, JSON.stringify(data))
}

function getMyWorkspaceThread(threadId) {
  return get(`/admin/my_workspace/inbox/threads/${encodeURIComponent(threadId)}`)
}

export function markMyWorkspaceThreadRead(threadId) {
  return post(`/admin/my_workspace/inbox/threads/${encodeURIComponent(threadId)}/read`, JSON.stringify({}))
}

function getMyWorkspaceAttachment(messageId, partId) {
  return get(`/admin/my_workspace/inbox/messages/${encodeURIComponent(messageId)}/attachments/${encodeURIComponent(partId)}`)
}

function getMyWorkspaceDrafts(pageToken = null) {
  const params = new URLSearchParams()
  if (pageToken) params.set('page_token', pageToken)
  return get(`/admin/my_workspace/inbox/drafts?${params}`)
}

function getMyWorkspaceDraft(draftId) {
  return get(`/admin/my_workspace/inbox/drafts/${encodeURIComponent(draftId)}`)
}

function getMyWorkspaceMessageDraft(messageId) {
  return get(`/admin/my_workspace/inbox/messages/${encodeURIComponent(messageId)}/draft`)
}

function saveMyWorkspaceDraft(data, draftId = null) {
  return draftId
    ? patch(`/admin/my_workspace/inbox/drafts/${encodeURIComponent(draftId)}`, JSON.stringify(data))
    : post('/admin/my_workspace/inbox/drafts', JSON.stringify(data))
}

function getMyWorkspaceSignature() {
  return get('/admin/my_workspace/inbox/signature')
}

function sendMyWorkspaceMessage(data, draftId = null) {
  return post(draftId ? `/admin/my_workspace/inbox/drafts/${encodeURIComponent(draftId)}/send` : '/admin/my_workspace/inbox/messages', JSON.stringify(data))
}

function trashMyWorkspaceThread(threadId, restore = false) {
  return post(`/admin/my_workspace/inbox/threads/${encodeURIComponent(threadId)}/${restore ? 'restore' : 'trash'}`, JSON.stringify({}))
}

function getMyWorkspaceCalendar({ timeMin, timeMax }) {
  const params = new URLSearchParams({ time_min: timeMin, time_max: timeMax })
  return get(`/admin/my_workspace/calendar?${params.toString()}`)
}

function getMyWorkspacePreviousMeeting(eventId, calendarId = 'primary') {
  const params = new URLSearchParams({ calendar_id: calendarId })
  return get(`/admin/my_workspace/calendar/events/${encodeURIComponent(eventId)}/previous_meeting?${params}`)
}

function createMyWorkspaceEvent(event) {
  return post('/admin/my_workspace/calendar/events', JSON.stringify(event))
}

function updateMyWorkspaceEvent(eventId, event) {
  return patch(`/admin/my_workspace/calendar/events/${encodeURIComponent(eventId)}`, JSON.stringify(event))
}

function deleteMyWorkspaceEvent(eventId, notifyGuests = true) {
  return deleteRequest(`/admin/my_workspace/calendar/events/${encodeURIComponent(eventId)}?notify_guests=${notifyGuests ? 'true' : 'false'}`)
}

function createTeamInboxMessage(data) {
  return post('/admin/team_inbox/messages', JSON.stringify(data))
}

function replyToTeamInboxThread(threadId, data) {
  return post(`/admin/team_inbox/threads/${encodeURIComponent(threadId)}/reply`, JSON.stringify(data))
}

function markTeamInboxThreadRead(threadId, mailbox) {
  return post(`/admin/team_inbox/threads/${encodeURIComponent(threadId)}/read`, JSON.stringify({ mailbox }))
}

function assignTeamInboxThreadToAlfred(threadId, mailbox) {
  return post(`/admin/team_inbox/threads/${encodeURIComponent(threadId)}/assign_to_alfred`, JSON.stringify({ mailbox }))
}

function getTeamInboxAlfredAnalysis(threadId, mailbox) {
  const params = new URLSearchParams()
  if (mailbox) params.set('mailbox', mailbox)
  return get(`/admin/team_inbox/threads/${encodeURIComponent(threadId)}/alfred_analysis?${params.toString()}`)
}

function getTeamInboxAttachment(messageId, attachment, mailbox) {
  const params = new URLSearchParams({
    filename: attachment.filename || 'attachment',
    mime_type: attachment.mime_type || 'application/octet-stream',
  })
  if (mailbox) params.set('mailbox', mailbox)
  return get(
    `/admin/team_inbox/messages/${encodeURIComponent(messageId)}/attachments/${encodeURIComponent(attachment.attachment_id)}?${params.toString()}`
  )
}

function updateTeamInboxWorkflow(threadId, mailbox, data) {
  return patch(`/admin/team_inbox/threads/${encodeURIComponent(threadId)}/workflow`, JSON.stringify({ ...data, mailbox }))
}

function acknowledgeTeamInboxThread(threadId, mailbox) {
  return post(`/admin/team_inbox/threads/${encodeURIComponent(threadId)}/acknowledge`, JSON.stringify({ mailbox }))
}

function closeTeamInboxThread(threadId, mailbox, reason) {
  return post(`/admin/team_inbox/threads/${encodeURIComponent(threadId)}/close`, JSON.stringify({ mailbox, reason }))
}

function reopenTeamInboxThread(threadId, mailbox) {
  return post(`/admin/team_inbox/threads/${encodeURIComponent(threadId)}/reopen`, JSON.stringify({ mailbox }))
}

function correctTeamInboxAlfredDecision(threadId, mailbox, data) {
  return post(`/admin/team_inbox/threads/${encodeURIComponent(threadId)}/correct_alfred`, JSON.stringify({ ...data, mailbox }))
}

function cancelTeamInboxFollowUp(followUpId, mailbox) {
  return deleteRequest(`/admin/team_inbox/follow_ups/${encodeURIComponent(followUpId)}?mailbox=${encodeURIComponent(mailbox)}`)
}

function createTeamInboxUploadUrl() {
  return post('/admin/team_inbox/create_upload_url', null)
}

async function uploadTeamInboxAttachment(uploadUrl, file) {
  const fullUrl = uploadUrl.startsWith('http') ? uploadUrl : `${BACKEND_URL}${uploadUrl}`
  const formData = new FormData()
  formData.append('file', file)
  return fetch(fullUrl, { method: 'POST', body: formData })
}

// EMAIL TASKS

function getEmailTasks(email, filter = 'active', page = 1) {
  return get(`/admin/email_tasks?email=${encodeURIComponent(email)}&filter=${filter}&page=${page}`)
}

function completeEmailTask(taskId) {
  return patch(`/admin/email_tasks/${taskId}/complete`)
}

function scanEmailTasks(email) {
  return post('/admin/email_tasks/scan', JSON.stringify({ email }))
}

function updateEmailTaskPriority(taskId, priorityOverride) {
  return patch(`/admin/email_tasks/${taskId}/update_priority`, JSON.stringify({ priority_override: priorityOverride }))
}

function markThreadPriority(email, gmailThreadId) {
  return post('/admin/email_tasks/mark_thread_priority', JSON.stringify({ email, gmail_thread_id: gmailThreadId }))
}

// ORGANIZATION SETTINGS

function organizationSettingShow() {
  return get('/admin/organization_setting', true);
}

function organizationSettingUpdate(data) {
  return patch('/admin/organization_setting', JSON.stringify({ organization_setting: data }), true);
}

// EMAIL HEALTH

function getEmailHealth(windowDays = 30, emailTypes, domain) {
  const query = new URLSearchParams({window: windowDays});
  if (domain) query.set('domain', domain);
  if (emailTypes) {
    if (emailTypes.length) emailTypes.forEach(type => query.append('email_types[]', type));
    else query.set('email_types', '');
  }
  return get(`/admin/email_health?${query}`);
}

function updateEmailLimits(data) {
  return patch('/admin/email_health/limits', JSON.stringify(data), true);
}

function getEmailWarmup(domain) {
  const time_zone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles';
  return get(`/admin/email_health/warmup?${new URLSearchParams({ domain, time_zone })}`);
}

function updateEmailWarmup(data) {
  return patch('/admin/email_health/warmup', JSON.stringify(data), true);
}

function getPostmasterHealth(windowDays = 30, domain) {
  const query = new URLSearchParams({ window: windowDays });
  if (domain) query.set('domain', domain);
  return get(`/admin/email_health/postmaster?${query}`);
}

function syncPostmasterHealth(domain) {
  return post('/admin/email_health/postmaster/sync', JSON.stringify({ domain }));
}

// COMMENTS

function listComments(parentType, parentId) {
  const base = parentType === 'seller' ? 'sellers' : 'buyers';
  return get(`/admin/${base}/${parentId}/comments`);
}

function createComment(parentType, parentId, body) {
  const base = parentType === 'seller' ? 'sellers' : 'buyers';
  return post(`/admin/${base}/${parentId}/comments`, JSON.stringify({ comment: { body } }));
}

function updateComment(commentId, body) {
  return patch(`/admin/comments/${commentId}`, JSON.stringify({ comment: { body } }));
}

function deleteComment(commentId) {
  return deleteRequest(`/admin/comments/${commentId}`);
}

// AGENTS

function listAgents() {
  return get('/admin/agents', true);
}

function getAgent(slug) {
  return get(`/admin/agents/${slug}`, true);
}

function listAgentRuns(slug, { limit = 50 } = {}) {
  return get(`/admin/agents/${slug}/runs?limit=${limit}`, true);
}

function getAgentSession(sessionId) {
  return get(`/admin/agent_sessions/${sessionId}`, true);
}

async function streamAgentSession(sessionId, signal) {
  const url = BACKEND_URL + `/admin/agent_sessions/${sessionId}/stream`;
  const opts = await formatHeaders(true, "GET");
  return fetch(url, { ...opts, signal });
}

function sendAgentSessionMessage(sessionId, text) {
  return post(
    `/admin/agent_sessions/${sessionId}/messages`,
    JSON.stringify({ text }),
    true
  );
}

function cancelAgentSession(sessionId) {
  return post(`/admin/agent_sessions/${sessionId}/cancel`, null, true);
}

function startAgentSessionStream(sessionId) {
  return post(`/admin/agent_sessions/${sessionId}/start_stream`, null, true);
}

function listAgentSessionFiles(sessionId) {
  return get(`/admin/agent_sessions/${sessionId}/files`, true);
}

// Streams the file via Rails proxy (which streams from Anthropic), turns
// the response into a Blob, and triggers a browser download. Inline
// because we need authenticated headers — a bare <a href> can't carry
// our encrypted admin API key.
async function downloadAgentSessionFile(sessionId, fileId, filename) {
  const url = `${BACKEND_URL}/admin/agent_sessions/${sessionId}/files/${fileId}`;
  const opts = await formatHeaders(true, 'GET');
  const res = await fetch(url, opts);
  if (!res.ok) throw new Error(`download failed: ${res.status}`);
  const blob = await res.blob();
  const objUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objUrl;
  a.download = filename || fileId;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Defer revoke so the click has time to start the download.
  setTimeout(() => URL.revokeObjectURL(objUrl), 1000);
}

function runAgent(slug) {
  return post(`/admin/agents/${slug}/run`, null, true);
}

function activateAgent(slug) {
  return post(`/admin/agents/${slug}/activate`, null, true);
}

function deactivateAgent(slug) {
  return post(`/admin/agents/${slug}/deactivate`, null, true);
}

function getAgentConfig(slug) {
  return get(`/admin/agents/${slug}/config`, true);
}

function startAgentBuilderSession({ scope = 'team' } = {}) {
  return post('/admin/agents/builder/start', JSON.stringify({ scope }), true);
}

// AGENT ESCALATIONS

function listAgentEscalations({ status = 'open', target_type = null, agent_slug = null, limit = 50 } = {}) {
  const params = new URLSearchParams({ status, limit });
  if (target_type) params.set('target_type', target_type);
  if (agent_slug) params.set('agent_slug', agent_slug);
  return get(`/admin/agent_escalations?${params.toString()}`, true);
}

function getAgentEscalation(escalationId) {
  return get(`/admin/agent_escalations/${escalationId}`, true);
}

function resolveAgentEscalation(escalationId, resolution_action, notes = '') {
  return post(
    `/admin/agent_escalations/${escalationId}/resolve`,
    JSON.stringify({ resolution_action, notes }),
    true
  );
}

function dismissAgentEscalation(escalationId, notes = '') {
  return post(
    `/admin/agent_escalations/${escalationId}/dismiss`,
    JSON.stringify({ notes }),
    true
  );
}

function startAgentEscalationChat(escalationId) {
  return post(`/admin/agent_escalations/${escalationId}/chat`, null, true);
}

function sendAgentEscalationChatMessage(escalationId, text) {
  return post(
    `/admin/agent_escalations/${escalationId}/chat/messages`,
    JSON.stringify({ text }),
    true
  );
}

function getAgentEscalationChatMessages(escalationId) {
  return get(`/admin/agent_escalations/${escalationId}/chat/messages`, true);
}

// DEAL AGENT CONFIGS
//
// Configures the deal-knowledge agent: which sellers it manages, which
// rag-system project holds each seller's vault, where digests go, and
// whether it's enabled. Distinct from getAgentConfig above, which is the
// unrelated Managed Agents fleet config.

function dealAgentConfigsIndex() {
  return get('/admin/agent_configs', true);
}

function dealAgentConfigCreate(payload) {
  return post('/admin/agent_configs', JSON.stringify(payload), true);
}

function dealAgentConfigUpdate(id, payload) {
  return patch('/admin/agent_configs/' + id, JSON.stringify(payload), true);
}

function dealAgentConfigDelete(id) {
  return deleteRequest('/admin/agent_configs/' + id, true);
}

// CLOSING CHECKLIST

function closingChecklistsIndex() {
  return get('/admin/closing_checklists', true);
}

function closingChecklistPreview(payload) {
  return post('/admin/closing_checklists/preview', JSON.stringify(payload), true);
}

function closingChecklistCreate(payload) {
  return post('/admin/closing_checklists', JSON.stringify(payload), true);
}

function closingChecklistUpdate(id, payload) {
  return patch(`/admin/closing_checklists/${id}`, JSON.stringify(payload), true);
}

function closingChecklistTaskCreate(checklistId, payload) {
  return post(`/admin/closing_checklists/${checklistId}/tasks`, JSON.stringify(payload), true);
}

function closingChecklistTaskUpdate(checklistId, taskId, payload) {
  return patch(`/admin/closing_checklists/${checklistId}/tasks/${taskId}`, JSON.stringify(payload), true);
}

function closingChecklistTaskDelete(checklistId, taskId) {
  return deleteRequest(`/admin/closing_checklists/${checklistId}/tasks/${taskId}`, true);
}

export {
  slackNotificationCreate,
  n8nWorkflowRun,
  buyersIndex, searchBuyerDatabase, buyerDatabaseMap, buyerShow, buyerApprove, buyerReject, buyerUpdate, buyerCreate,
  sellersIndex, sellerBuyers, sellerBuyersAll, sellerShow, sellerGenerateSalesDeck, sellerEnrichSeller, sellerSendConfidentialityAgreement, sellerSendPreMeetingDiscoveryEmail, sellerSendValuationRequestEmail, sellerSetupLlamaCloudIndex, sellerRagWebsite, sellerRagStatus, sellerRagSyncNow, sellerEnrichFromWebsite, sellerSyncGoogleDrive, sellerGenerateExitNavigator, sellerCreate, sellerUpdate, sellerDelete, sellerChecklistUpdate, sellerScrapeData, sellerExitAuditCreate, sellerDealStudioSync, convertSellerToBuyer, sellerDealroomAnalytics, buyerDealroomAnalytics,
  sellerBuyerOutreach, sellerBuyerOutreachUpdate, sellerBuyerOutreachCreateRow, sellerBuyerOutreachUpdateRow, sellerBuyerOutreachDeleteRow, sellerBuyerOutreachBulkDeleteRows, sellerBuyerOutreachBulkCreateRows, sellerBuyerOutreachCreateColumn, sellerBuyerOutreachUpdateColumn, sellerBuyerOutreachCreateActivity, sellerBuyerOutreachImportCsv, sellerBuyerOutreachExportCsv, sellerBuyerOutreachStartResearchRun, sellerBuyerOutreachLatestResearchRun,
  confirmQuestionRelevance, triageAlfredQuestion, undoAlfredQuestionDigest, previewQuestionRelevance, checkQuestionRelevance, getQuestionRelevanceRun, cancelQuestionRelevanceRun,
  getAlfredDealDashboard, getAlfredSellers, getAlfredSeller, generateAlfredDealReport, getAlfredQuestion, getAgentQuestions, getAlfredOperations, getAlfredOutbox, getAlfredSettings, saveAlfredSettings, getAlfredTaskSettings, saveAlfredTaskSetting, getDriveOrganizer, saveDriveOrganizerSetting, runDriveOrganizer, messageDriveOrganizerRun, approveDriveOrganizerRun, rejectDriveOrganizerRun, getAlfredConversationPeople, getAlfredConversationTimeline, getAlfredCrmBatches, previewAlfredCrmBatch, startAlfredCrmBatch, prepareAlfredCrmReplacement, retryAlfredCrmExecution, retryAlfredCrmBatch, restoreAlfredCrmBatchEntry, getAlfredWorkItems, getAlfredWorkItem, getCrmAmendmentSchema, amendAlfredWorkItem, decideAlfredWorkItem, askAlfredWorkItemNow, dismissAlfredWorkItem, reassignAlfredWorkItem, previewAlfredWorkItemRecipientRepair, routeAlfredWorkItemQuestion, sendAlfredWorkItemFollowUpNow, createQuestionTestDelivery, getQuestionTestDelivery, getQuestionTestDeliveries, createAgentQuestionDigest, draftAgentQuestionDigest, previewAgentQuestionDigest, saveAgentQuestionDigestDraft, getAlfredQuestionDigests, getAlfredRouteChoices, resolveAlfredRouteChoice, controlAlfredConversationRun, answerAlfredQuestion, replyToAlfredQuestionAsOwner, dismissAlfredQuestion, reconcileAlfredQuestionClosure, reopenAlfredQuestion, retryAlfredQuestion, deliverAgentQuestion, recallAgentQuestionDelivery, createAsk, sendAlfredConversationMessage, getAskOptions, getAskReadiness, approveAsk, draftAlfredQuestion, draftAlfredWorkItem, reviseAsk, revoiceAsk, closeAsk, getAlfredActivity, getAlfredBrain, getAlfredBrainNote, syncAlfredBrainFromNotion, getBrainChat, getBrainChatHistory, resetBrainChat, sendBrainChatMessage, getBrainProposals, approveBrainProposal, rejectBrainProposal, updateAlfredScorecardCell, updateAlfredSeller, updateAlfredSellerEosFact, updateAlfredSellerPayouts, dealPodsIndex, dealPodCreate, dealPodUpdate, exitAuditShow,
  driveFilesIndex,
  dataroomUpdate,
  dealsIndex, dealUpdate, dealChecklistUpdate, taskUpdate,
  exitNavigatorUpdate, exitNavigatorGenerate,
  breakwaterDriveUpdate,
  ragStoreUpdate,
  stytchAuth,
  employeesIndex, employeeCreate, showEmployee, employeeUpdate, employeeProfileShow, employeeProfileUpdate, employeeDeals, updatePartnerSettings,
  partnersIndex, partnerCreate, partnerUpdate, partnerDestroy,
  contactSourcesIndex, contactSourceCreate, contactSourceUpdate, contactSourceDestroy,
  leadEntryPointsIndex, leadEntryPointCreate, leadEntryPointUpdate, leadEntryPointDestroy, contactsIndex, contactCreate,
  referralPartnersIndex, referralPartnerCreate, referralPartnerAddContact, referrersIndex,
  organizationSettingShow, organizationSettingUpdate, getEmailHealth, updateEmailLimits, getEmailWarmup, updateEmailWarmup, getPostmasterHealth, syncPostmasterHealth,
  findDealPartnerByEmail, localDashboardAuth, getMyTasks, getPriorityTasks, reassignTask, updateTaskField, taskCompleteWithHandler, bulkCatchUpTasks, jumpToTask, getSellerDealPartners, getMyMetrics, updateMyMetricsSeller, updateMyMetricsSellerEosFact, updateMyMetricsSellerPayouts, dashboardSearch,
  previewEmail, sendEmail, createEmailUploadUrl, uploadEmailAttachment,
  createGenericUploadUrl, uploadGenericFile, browseDriveFolder, createDriveFolder, setupSellerDrive, createSellerDealroom, getDriveFile, deleteDriveItem, removeLlamaCloudKnowledgeFile, updateDriveItemSyncSettings, renameDealroomItem, moveDriveItem, retryDriveItemSync, uploadDriveFile, stageDriveUpload, listDriveUploads, uploadDealroomFile, replaceDealroomFileVersion, uploadLlamaCloudKnowledgeFile, downloadDriveFile, previewSlackChannel, getDealTemplate,
  listAllDealPartners, assignSellerDealPartners, unassignDealPartnerFromSeller,
  getMyMeetingNotes, getMeetingNote, updateMeetingNote, createMeetingRecordingPublicLink, getPublicMeetingRecording, unlockPublicMeetingRecording, updateMeetingNoteTask, shareMeetingNote, unshareMeetingNote,
  getGmailSignature, getGmailMessages, getGmailThread, sendGmailReply, deleteGmailMessage, markGmailThreadRead, searchGmailPriorThreads,
  getTeamInboxSignature, getTeamInboxMailboxes, createTeamInboxMailbox, updateTeamInboxMailbox,
  getMyWorkspaceWritingStyle, saveMyWorkspaceWritingStyle, analyseMyWorkspaceWritingStyle, getMyWorkspaceWritingStyleAnalysis, getMyWorkspaceEmailRules, saveMyWorkspaceEmailRules,
  getMyWorkspaceDrafts, getMyWorkspaceDraft, getMyWorkspaceMessageDraft, saveMyWorkspaceDraft, sendMyWorkspaceMessage, trashMyWorkspaceThread, getMyWorkspaceSignature,
  getTeamInboxThreads, getTeamInboxThread, getMyWorkspaceInbox, getMyWorkspaceClients, getMyWorkspaceClientThreads, getMyWorkspaceLabels, getMyWorkspaceThread, getMyWorkspaceAttachment, getMyWorkspaceCalendar, getMyWorkspacePreviousMeeting, createMyWorkspaceEvent, updateMyWorkspaceEvent, deleteMyWorkspaceEvent, createTeamInboxMessage, replyToTeamInboxThread,
  markTeamInboxThreadRead, assignTeamInboxThreadToAlfred, getTeamInboxAlfredAnalysis,
  getTeamInboxAttachment, updateTeamInboxWorkflow, acknowledgeTeamInboxThread, closeTeamInboxThread,
  reopenTeamInboxThread, correctTeamInboxAlfredDecision, cancelTeamInboxFollowUp, createTeamInboxUploadUrl, uploadTeamInboxAttachment,
  getEmailTasks, completeEmailTask, scanEmailTasks, updateEmailTaskPriority, markThreadPriority,
  listComments, createComment, updateComment, deleteComment,
  listAgents, getAgent, listAgentRuns, getAgentSession, sendAgentSessionMessage,
  cancelAgentSession, runAgent, activateAgent, deactivateAgent, getAgentConfig,
  startAgentBuilderSession, streamAgentSession, startAgentSessionStream,
  listAgentSessionFiles, downloadAgentSessionFile,
  listAgentEscalations, getAgentEscalation, resolveAgentEscalation, dismissAgentEscalation,
  startAgentEscalationChat, sendAgentEscalationChatMessage, getAgentEscalationChatMessages,
  dealAgentConfigsIndex, dealAgentConfigCreate, dealAgentConfigUpdate, dealAgentConfigDelete,
  closingChecklistsIndex, closingChecklistPreview, closingChecklistCreate, closingChecklistUpdate,
  closingChecklistTaskCreate, closingChecklistTaskUpdate, closingChecklistTaskDelete,
  _setBackendStoreRef, _setCurrentDealPartnerId, _getCurrentDealPartnerId
}


// NDA review actions keep draft generation separate from the human send.
export async function summarizeNdaReview(dealId, reviewVersion, signal) {
  const path = `/admin/nda_reviews/${encodeURIComponent(dealId)}/reasoning_summary`;
  return fetch(BACKEND_URL + path, { ...await formatHeaders(false, 'POST', JSON.stringify({review_version:reviewVersion}), ndaTransportHeaders(), path), cache: 'no-store', signal });
}
export const getNdaReview = (dealId, { includeSignature = false } = {}) => get(`/admin/nda_reviews/${dealId}${includeSignature ? '?include_signature=true' : ''}`, false, ndaTransportHeaders());
export const addNdaContext = (dealId, payload) => post(`/admin/nda_reviews/${dealId}/context`, JSON.stringify(payload), false, ndaTransportHeaders());
export const askNdaPartner = (dealId, payload) => post(`/admin/nda_reviews/${dealId}/ask_partner`, JSON.stringify(payload), false, ndaTransportHeaders());
export const requestNdaInformationDraft = (dealId, reviewVersion, options = {}) => post(`/admin/nda_reviews/${dealId}/draft`, JSON.stringify({ review_version: reviewVersion, ...options }), false, ndaTransportHeaders());
export const sendNdaInformationRequest = (dealId, payload) => post(`/admin/nda_reviews/${dealId}/send_email`, JSON.stringify(payload), false, ndaTransportHeaders());
export const reconcileNdaInformationRequest = (dealId, reviewVersion) => post(`/admin/nda_reviews/${dealId}/reconcile`, JSON.stringify({ review_version: reviewVersion }), false, ndaTransportHeaders());
export const reassessNdaReview = (dealId, reviewVersion, response) => post(`/admin/nda_reviews/${dealId}/reassess`, JSON.stringify({ review_version: reviewVersion, response }), false, ndaTransportHeaders());
export const holdNdaReview = (dealId, reviewVersion) => post(`/admin/nda_reviews/${dealId}/hold`, JSON.stringify({ review_version: reviewVersion }), false, ndaTransportHeaders());
export const resumeNdaReview = (dealId, reviewVersion) => post(`/admin/nda_reviews/${dealId}/resume`, JSON.stringify({ review_version: reviewVersion }), false, ndaTransportHeaders());
export const reevaluateNdaReview = (dealId, reviewVersion, context) => post(`/admin/nda_reviews/${dealId}/reevaluate`, JSON.stringify({ review_version: reviewVersion, context }), false, ndaTransportHeaders());

// Seller-scoped campaign requests share the authenticated admin transport.
export const campaignRequest = async (sellerId, path = '', method = 'GET', payload) => {
  const url = `/admin/sellers/${encodeURIComponent(sellerId)}/campaigns${path}`;
  const session = ndaTransportHeaders();
  const response = method === 'GET' ? await get(url, false, session)
    : method === 'PATCH' ? await patch(url, JSON.stringify(payload), false, session)
    : method === 'DELETE' ? await deleteRequest(url, JSON.stringify(payload), false, session)
    : await post(url, JSON.stringify(payload), false, session);
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Could not update campaign.');
  return data;
};

export const getNdaRejectionPreview = (dealId) => get(`/admin/nda_reviews/${dealId}/rejection_preview`, false, ndaTransportHeaders());
export const rejectNdaReview = (dealId, data) => post(`/admin/nda_reviews/${dealId}/reject`, JSON.stringify(data), false, ndaTransportHeaders());
export const recoverNdaRejection = (dealId, data) => post(`/admin/nda_reviews/${dealId}/rejection_delivery`, JSON.stringify(data), false, ndaTransportHeaders());

export async function buyerCompanyResearch(buyerId, method = 'GET', payload) {
  const path = `/admin/buyers/${buyerId}/company_enrichment`;
  const body = payload === undefined ? null : JSON.stringify(payload);
  const response = method === 'POST' ? await post(path, body) : method === 'PATCH' ? await patch(path, body) : await get(path);
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Could not load company research.');
  return data;
}

export const getSellerCorrespondence = sellerId => get(`/admin/my_workspace/correspondence/${encodeURIComponent(sellerId)}`)
export const updateSellerCorrespondence = (sellerId, data) => patch(`/admin/my_workspace/correspondence/${encodeURIComponent(sellerId)}`, JSON.stringify(data))
export const reviewSellerCorrespondence = (sellerId, revision) => post(`/admin/my_workspace/correspondence/${encodeURIComponent(sellerId)}/review`, JSON.stringify({ revision }))
export const saveCorrespondenceHours = working_hours => patch('/admin/my_workspace/correspondence_settings', JSON.stringify({ working_hours }))

export async function cimRequest(path = '', method = 'GET', payload) {
  const url = `/admin/cim_decks${path}`;
  const response = method === 'GET' ? await get(url) : await post(url, JSON.stringify(payload || {}));
  const body = await response.json();
  if (!response.ok) {
    const error = new Error(body.error || 'Could not update CIM.');
    error.status = response.status;
    throw error;
  }
  return body;
}

export async function cimGenerationRequest(path = '', method = 'GET', payload) {
  const url = `/admin/cim_generations${path}`;
  const response = method === 'GET' ? await get(url) : await post(url, JSON.stringify(payload || {}));
  const body = await response.json();
  if (!response.ok) {
    const error = new Error(body.error || 'Could not update CIM generation.');
    error.status = response.status;
    throw error;
  }
  return body;
}

export async function cimUploadSource(deckId, file, uploadKey, sourceKey) {
  const form = new FormData();
  form.append('file', file);
  form.append('uploadKey', uploadKey);
  if (sourceKey) form.append('sourceKey', sourceKey);
  const options = await formatHeaders(false, 'POST', form);
  delete options.headers['Content-Type'];
  const response = await fetch(`${BACKEND_URL}/admin/cim_decks/${deckId}/upload_source`, options);
  const body = await response.json();
  if (!response.ok) throw new Error(body.error || 'Could not upload source.');
  return body;
}

export async function cimDownloadSource(deckId, sourceId, filename) {
  const response = await get(`/admin/cim_decks/${deckId}/sources/${sourceId}/download`);
  if (!response.ok) throw new Error('Could not download this source.');
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

export const getMessageFollowUps = messageId => get(`/admin/my_workspace/message_follow_ups?message_id=${encodeURIComponent(messageId)}`)
export const getScheduledMessageFollowUps = (pageToken, kind, includeCancelled = true) => get(`/admin/my_workspace/message_follow_ups?view=scheduled&kind=${encodeURIComponent(kind)}${kind === 'nudge' ? `&include_cancelled=${includeCancelled}` : ''}${pageToken ? `&page_token=${encodeURIComponent(pageToken)}` : ''}`)
export const createMessageFollowUp = data => post('/admin/my_workspace/message_follow_ups', JSON.stringify(data))
export const updateMessageFollowUp = (id, data) => patch(`/admin/my_workspace/message_follow_ups/${encodeURIComponent(id)}`, JSON.stringify(data))
export const cancelMessageFollowUp = (id, revision) => deleteRequest(`/admin/my_workspace/message_follow_ups/${encodeURIComponent(id)}`, JSON.stringify({ revision }))

export const getMyWorkspaceToday = () => get("/admin/my_workspace/today")
export const generateMyWorkspaceToday = () => post("/admin/my_workspace/today", JSON.stringify({}))

export const getMessageFollowUpCounts = () => get('/admin/my_workspace/message_follow_ups?view=counts')

export const discardMyWorkspaceDraft = (draftId, version) => deleteRequest(`/admin/my_workspace/inbox/drafts/${encodeURIComponent(draftId)}`, JSON.stringify({ version }))

export const getPersonalAlfredSetup = () => get('/admin/workspace_agent_connections/onboarding')
export const startPersonalAlfredSetup = () => post('/admin/workspace_agent_connections/onboarding_start', JSON.stringify({}))
export const retryPersonalAlfredSetup = () => post('/admin/workspace_agent_connections/onboarding_retry', JSON.stringify({}))
export const connectPersonalAlfredApp = (toolkit, action_type) => post('/admin/workspace_agent_connections/onboarding_app', JSON.stringify({ toolkit, action_type }))
export const checkPersonalAlfredSetup = () => post('/admin/workspace_agent_connections/onboarding_check', JSON.stringify({}))
export const cancelPersonalAlfredSetup = () => deleteRequest('/admin/workspace_agent_connections/onboarding_cancel')

export const getWorkspaceAgentSetup = () => get('/admin/workspace_agent_connections/setup')
export const connectWorkspaceAgent = () => post('/admin/workspace_agent_connections/connect', JSON.stringify({}))
export const disconnectWorkspaceAgent = () => deleteRequest('/admin/workspace_agent_connections/disconnect')

export function lookupMyWorkspaceGuests(emails) {
  const query = new URLSearchParams()
  emails.forEach(email => query.append('emails[]', email))
  return get(`/admin/my_workspace/calendar/guest_contacts?${query}`)
}

export function bookMyWorkspaceClientMeeting(payload) {
  return post('/admin/my_workspace/calendar/bookings', JSON.stringify(payload))
}

export async function transcribeMyWorkspaceAudio(audio, signal) {
  const path = '/admin/my_workspace/audio/transcription'
  const body = new FormData()
  const extension = audio.type.includes('mp4') ? 'm4a' : audio.type.includes('ogg') ? 'ogg' : 'webm'
  body.append('audio', audio, `recording.${extension}`)
  const options = await formatHeaders(false, 'POST', body, {}, path)
  delete options.headers['Content-Type']
  return fetch(BACKEND_URL + path, {...options, signal})
}

async function workspaceNativeManagerRequest(action, signal) {
  const path = `/admin/my_workspace/partner_connection/${action}`
  return fetch(BACKEND_URL + path, { ...await formatHeaders(false, 'POST', JSON.stringify({}), {}, path), cache: 'no-store', signal })
}
export const openWorkspaceNativeMcpManager = signal => workspaceNativeManagerRequest('native_manager', signal)

export const checkWorkspaceNativeMcpManager = signal => workspaceNativeManagerRequest('native_manager_status', signal)

export const reloadWorkspaceNativeMcp = () => post('/admin/my_workspace/partner_connection/native_reload', JSON.stringify({}))

// Mandate workflow never accepts free-form outgoing email content.
export const buyerMandateRelationships = buyerId => get(`/admin/buyer_mandate_relationships${buyerId ? `?buyer_id=${encodeURIComponent(buyerId)}` : ''}`)
export const buyerMandateRelationship = id => get(`/admin/buyer_mandate_relationships/${encodeURIComponent(id)}`)
export const buyerMandateCreate = data => post('/admin/buyer_mandate_relationships', JSON.stringify(data))
export const buyerMandateAction = (id, action, data = {}) => {
  if (!['pause', 'resume', 'takeover', 'review', 'approve_delivery', 'retry_run', 'request_draft'].includes(action)) throw new Error('Invalid mandate action')
  return post(`/admin/buyer_mandate_relationships/${encodeURIComponent(id)}/${action}`, JSON.stringify(data))
}

export const buyerMandateDownload = async (id, sourceId, filename) => {
  const path = `/admin/buyer_mandate_relationships/${encodeURIComponent(id)}/source?source_id=${encodeURIComponent(sourceId)}`
  const response = await get(path)
  if (!response.ok) throw new Error('The original document could not be downloaded.')
  const url = URL.createObjectURL(await response.blob())
  const link = document.createElement('a')
  link.href = url; link.download = filename || 'buyer-document'
  document.body.appendChild(link); link.click(); link.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

// Discovery accepts research and reviewed handoff actions, never outgoing text.
export async function buyerDiscoveryRequest(path = '', { method = 'GET', body, signal } = {}) {
  const session = taskSkillSessionHeaders()
  const options = await formatHeaders(true, method, body == null ? null : JSON.stringify(body))
  Object.assign(options.headers, session)
  const response = await fetch(`${BACKEND_URL}/admin/buyer_discovery${path}`, { ...options, signal })
  const payload = await response.json().catch(() => ({}))
  if (!response.ok) {
    const error = new Error(payload.error || 'Could not update buyer discovery. Try again.')
    error.status = response.status
    error.payload = payload
    throw error
  }
  return payload
}

// Server-owned seller research; authenticate the actor separately from selected employee.
export async function sellerProspectRequest(path = '', method = 'GET', payload) {
  const url = `/admin/seller_prospect_searches${path}`
  const session = taskSkillSessionHeaders()
  const body = payload === undefined ? null : JSON.stringify(payload)
  const response = method === 'GET' ? await get(url, true, session)
    : method === 'PATCH' ? await patch(url, body, true, session) : await post(url, body, true, session)
  const result = await response.json().catch(() => ({}))
  if (!response.ok) {
    const error = new Error(result.error || 'Could not load seller prospects. Try again.')
    error.status = response.status
    error.code = result.code
    throw error
  }
  return result
}

export async function seoDashboardRequest({ source = 'search', days = 28, signal } = {}) {
  const options = await formatHeaders(true, 'GET')
  Object.assign(options.headers, taskSkillSessionHeaders())
  const response = await fetch(`${BACKEND_URL}/admin/seo_dashboard?${new URLSearchParams({ source, days })}`, { ...options, signal, cache: 'no-store' })
  const payload = await response.json().catch(() => ({}))
  if (!response.ok) throw new Error(payload.error || 'Could not load website traffic.')
  return payload
}
